/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import TreeView from "@material-ui/lab/TreeView";
import TreeItem from "@material-ui/lab/TreeItem";
import Typography from "@material-ui/core/Typography";
import ButtonBase from "@material-ui/core/ButtonBase";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import FolderIcon from "@material-ui/icons/Folder";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import BusinessIcon from "@material-ui/icons/Business";
import PeopleIcon from "@material-ui/icons/People";
import PersonIcon from "@material-ui/icons/Person";

import Grid from "components/Grid/GridContainer";
import Col from "components/Grid/GridItem";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Input,
  Label,
  Table
} from "reactstrap";
import moment from "moment";
import "moment/locale/th";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";

// รายชื่อและข้อมูลของ 4 รองภารกิจหลัก
const DEPUTY_CONFIG = {
  supattra: {
    missionKey: 1,
    missionName: "ภารกิจด้านวิชาการและการแพทย์",
    deputyTitle: "แพทย์หญิงสุพัตรา ชาติลีฬหา",
    position: "นายแพทย์ชำนาญการพิเศษ",
    color: "#0284c7"
  },
  prapatsorn: {
    missionKey: 2,
    missionName: "ภารกิจด้านอำนวยการ",
    deputyTitle: "นางประภัสสร บุญเจริญ",
    position: "นักวิชาการพัสดุชำนาญการ",
    color: "#7c3aed"
  },
  nirumon: {
    missionKey: 3,
    missionName: "ภารกิจด้านการพยาบาล",
    deputyTitle: "นางนิฤมล ศรีโยธา",
    position: "พยาบาลวิชาชีพชำนาญการพิเศษ",
    color: "#059669"
  },
  siriporn: {
    missionKey: 5,
    missionName: "ภารกิจด้านการพัฒนาระบบสุขภาพ",
    deputyTitle: "นางสาวศิริพร แสงหิรัญ",
    position: "นักวิชาการสาธารณสุขชำนาญการ",
    color: "#ea580c"
  }
};

const DEV_ACCOUNTS = ["madarakung", "ton900", "admin"];

const ALL_MISSIONS = [
  { key: 0, name: "🏢 ทั้งหมด (ภาพรวมทั้งโรงพยาบาล)", color: "#1e293b" },
  { key: 1, name: "🔬 ภารกิจด้านวิชาการและการแพทย์", color: "#0284c7" },
  { key: 2, name: "💼 ภารกิจด้านอำนวยการ", color: "#7c3aed" },
  { key: 3, name: "🏥 ภารกิจด้านการพยาบาล", color: "#059669" },
  { key: 5, name: "🌐 ภารกิจด้านการพัฒนาระบบสุขภาพ", color: "#ea580c" },
  { key: 6, name: "🏛️ สำนักงานผู้อำนวยการ", color: "#475569" }
];

const MONTH_NAMES_THAI = [
  "ต.ค.", "พ.ย.", "ธ.ค.", "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย."
];
const MONTH_NUMBERS = [10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8, 9];

// Helpers สำหรับจัดรูปแบบวันที่ พ.ศ.
const formatThaiDate = (dateStr) => {
  if (!dateStr) return "-";
  const m = moment(dateStr);
  if (!m.isValid()) return "-";
  return `${m.date()} ${m.locale("th").format("MMM")} ${m.year() + 543}`;
};

const formatThaiDateRange = (startStr, endStr) => {
  if (!startStr) return "-";
  const ms = moment(startStr);
  const me = moment(endStr || startStr);
  const startTh = `${ms.date()} ${ms.locale("th").format("MMM")}`;
  const endTh = `${me.date()} ${me.locale("th").format("MMM")} ${me.year() + 543}`;
  if (ms.isSame(me, "day")) {
    return `${startTh} ${ms.year() + 543}`;
  }
  return `${startTh} - ${endTh}`;
};

// TreeItem Styles (แบบเดียวกับใน TSUser / ระบบวันลา & จองห้องประชุม แต่ปรับเป็นสีดำเข้มชัดเจน)
const useTreeItemStyles = makeStyles((theme) => ({
  root: {
    color: "#000000 !important",
    "&:hover > $content": {
      backgroundColor: "#e2e8f0"
    },
    "&:focus > $content, &$selected > $content": {
      backgroundColor: `var(--tree-view-bg-color, #e2e8f0)`,
      color: "#000000 !important"
    },
    "&:focus > $content $label, &:hover > $content $label, &$selected > $content $label": {
      backgroundColor: "transparent"
    }
  },
  content: {
    color: "#000000 !important",
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    paddingRight: theme.spacing(1),
    paddingTop: 3,
    paddingBottom: 3,
    fontWeight: "600",
    "$expanded > &": {
      fontWeight: "bold"
    }
  },
  group: {
    marginLeft: 14,
    "& $content": {
      paddingLeft: theme.spacing(1)
    }
  },
  expanded: {},
  selected: {},
  label: {
    fontWeight: "bold",
    color: "#000000 !important"
  },
  labelRoot: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 0)
  },
  labelIcon: {
    marginRight: theme.spacing(1),
    fontSize: "1.25rem"
  },
  labelText: {
    fontWeight: "600",
    flexGrow: 1,
    fontSize: "0.93rem",
    fontFamily: '"Sarabun", sans-serif',
    color: "#000000 !important"
  }
}));

function StyledTreeItem(props) {
  const classes = useTreeItemStyles();
  const { labelText, labelIcon: LabelIcon, labelInfo, color, bgColor, onTreeClick, ...other } = props;

  // ตัดเครื่องหมาย # หรือ #### นำหน้าชื่อหน่วยงาน/กลุ่มงานออก เพื่อให้อ่านง่ายและสวยงาม
  const cleanLabelText = typeof labelText === "string" ? labelText.replace(/^#+/, "").trim() : labelText;

  return (
    <TreeItem
      onLabelClick={onTreeClick}
      label={
        <div className={classes.labelRoot}>
          {LabelIcon && <LabelIcon style={{ color: color || "#000000" }} className={classes.labelIcon} />}
          <Typography variant="body2" className={classes.labelText} style={{ color: "#000000", fontWeight: "600" }}>
            {cleanLabelText}
          </Typography>
          {labelInfo && (
            <Typography variant="caption" style={{ color: "#000000", fontWeight: "bold" }}>
              {labelInfo}
            </Typography>
          )}
        </div>
      }
      style={{
        "--tree-view-color": color || "#000000",
        "--tree-view-bg-color": bgColor || "#e2e8f0"
      }}
      classes={{
        root: classes.root,
        content: classes.content,
        expanded: classes.expanded,
        selected: classes.selected,
        group: classes.group,
        label: classes.label
      }}
      {...other}
    />
  );
}

StyledTreeItem.propTypes = {
  bgColor: PropTypes.string,
  color: PropTypes.string,
  labelIcon: PropTypes.elementType,
  labelInfo: PropTypes.string,
  labelText: PropTypes.string.isRequired,
  onTreeClick: PropTypes.func
};

const useStyles = makeStyles({
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "600",
    fontFamily: '"Sarabun", sans-serif',
    marginBottom: "3px",
    textDecoration: "none"
  },
  cardCategoryWhite: {
    color: "rgba(255,255,255,.85)",
    margin: "0",
    fontSize: "13px",
    marginTop: "2px",
    marginBottom: "0",
    fontFamily: '"Sarabun", sans-serif'
  },
  treeRoot: {
    flexGrow: 1,
    overflowY: "auto",
    maxHeight: "340px",
    padding: "6px"
  },
  // การ์ดผู้ใช้งานแบบเดียวกับ TSUser แต่ปรับดีไซน์ให้โมเดิร์น สวยงาม พรีเมียม
  userCardSm: {
    width: 156,
    minHeight: 148,
    textAlign: "center",
    margin: "6px",
    padding: "12px 8px",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    display: "inline-block",
    borderRadius: "12px",
    cursor: "pointer",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 6px -1px rgba(0,0,0,0.05), 0 1px 3px -1px rgba(0,0,0,0.03)",
    "&:hover": {
      boxShadow: "0 10px 20px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
      transform: "translateY(-3px)",
      borderColor: "#0284c7"
    }
  },
  userCardSmSelected: {
    width: 156,
    minHeight: 148,
    textAlign: "center",
    margin: "6px",
    padding: "12px 8px",
    display: "inline-block",
    borderRadius: "12px",
    cursor: "pointer",
    backgroundColor: "#f0fdf4",
    border: "2px solid #16a34a",
    boxShadow: "0 6px 16px -2px rgba(22, 163, 74, 0.25)",
    transform: "translateY(-2px)"
  },
  cardImg: {
    width: 66,
    height: 66,
    borderRadius: "50%",
    objectFit: "cover",
    objectPosition: "100% 0",
    marginBottom: "8px",
    border: "2px solid #e2e8f0",
    boxShadow: "0 2px 4px rgba(0,0,0,0.06)"
  }
});

let activeScrollRaf = null;

export default function MissionDashboard() {
  const classes = useStyles();
  moment.locale("th");

  // User & Role State
  const [currentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("currentUser") || "{}");
    } catch (e) {
      return {};
    }
  });

  const userName = (currentUser.userName || "").toLowerCase();
  const userDepts = Array.isArray(currentUser.userDepartment) ? currentUser.userDepartment : [];

  // สิทธิ์งานคอมพิวเตอร์ (dept 108) และ HR (dept 105)
  const isIT = userDepts.some((u) => u.departmentKey === 108);
  const isHR = userDepts.some(
    (u) => u.departmentKey === 105 || (u.departmentKey === 143 && u.userLevelKey === 3)
  );
  const isDev = DEV_ACCOUNTS.includes(userName) || currentUser.userLevelKey === -1;

  // สิทธิ์ดูได้ทุกภารกิจ (งานคอม, HR, Dev/Admin) มีสิทธิ์เท่ากัน 100%
  const hasFullAccess = isDev || isIT || isHR;

  // รองภารกิจ
  const isDeputy = Boolean(DEPUTY_CONFIG[userName]);
  const userDeputyInfo = DEPUTY_CONFIG[userName];
  const isDeputyOnly = isDeputy && !hasFullAccess;

  // คำนวณปีงบประมาณเริ่มต้นเป็น พ.ศ. ทั้งหมด (เริ่ม ต.ค. คือปีงบถัดไป)
  const currentCalYear = moment().month() >= 9 ? moment().year() + 544 : moment().year() + 543;
  const defaultStartDate = `${currentCalYear - 544}-10-01`;
  const defaultEndDate = `${currentCalYear - 543}-09-30`;

  const [selectedYear, setSelectedYear] = useState(() => {
    try {
      const saved = sessionStorage.getItem("mission_selectedYear");
      if (saved) return parseInt(saved, 10);
    } catch (e) {}
    return currentCalYear;
  });

  // ตัวกรองช่วงเวลา: "fiscal" (ตามปีงบประมาณ พ.ศ.) หรือ "custom" (กำหนดช่วงวันที่เอง)
  const [filterMode, setFilterMode] = useState(() => {
    try {
      const saved = sessionStorage.getItem("mission_filterMode");
      if (saved === "custom" || saved === "fiscal") return saved;
    } catch (e) {}
    return "fiscal";
  });

  // วันที่เริ่มต้น และสิ้นสุดสำหรับโหมดกำหนดช่วงวันที่เอง (default: 1 ต.ค. - 30 ก.ย. ของปีงบปัจจุบัน)
  const [customStartDate, setCustomStartDate] = useState(() => {
    try {
      const saved = sessionStorage.getItem("mission_customStartDate");
      if (saved) return saved;
    } catch (e) {}
    return defaultStartDate;
  });

  const [customEndDate, setCustomEndDate] = useState(() => {
    try {
      const saved = sessionStorage.getItem("mission_customEndDate");
      if (saved) return saved;
    } catch (e) {}
    return defaultEndDate;
  });

  // บันทึกตัวกรองลง sessionStorage ทุกครั้งที่มีการเปลี่ยนแปลง เพื่อให้ใช้ตลอดไปจนกว่าจะเคลียร์ค่า
  useEffect(() => {
    try {
      sessionStorage.setItem("mission_filterMode", filterMode);
      sessionStorage.setItem("mission_selectedYear", String(selectedYear));
      if (customStartDate) sessionStorage.setItem("mission_customStartDate", customStartDate);
      if (customEndDate) sessionStorage.setItem("mission_customEndDate", customEndDate);
    } catch (e) {}
  }, [filterMode, selectedYear, customStartDate, customEndDate]);

  // ฟังก์ชันเคลียร์ค่าตัวกรองกลับสู่ปีงบประมาณปัจจุบัน
  const handleClearFilter = () => {
    try {
      sessionStorage.removeItem("mission_filterMode");
      sessionStorage.removeItem("mission_customStartDate");
      sessionStorage.removeItem("mission_customEndDate");
      sessionStorage.removeItem("mission_selectedYear");
    } catch (e) {}
    setFilterMode("fiscal");
    setSelectedYear(currentCalYear);
    setCustomStartDate(defaultStartDate);
    setCustomEndDate(defaultEndDate);
  };

  // รายการปีงบประมาณ พ.ศ. ล้วน
  const yearOptions = useMemo(() => {
    const opts = [];
    for (let y = currentCalYear + 1; y >= currentCalYear - 5; y--) {
      opts.push({
        value: y,
        label: `ปีงบประมาณ พ.ศ. ${y} (1 ต.ค. ${y - 1} - 30 ก.ย. ${y})`
      });
    }
    return opts;
  }, [currentCalYear]);

  // Mission State
  const [selectedMission, setSelectedMission] = useState(() => {
    if (isDeputyOnly && userDeputyInfo) return userDeputyInfo.missionKey;
    return 0; // Default: All missions for Full Access (IT / HR / Dev)
  });

  // มุมมองที่เลือก: "ALL" = ภาพรวม, หรือ userName ของบุคลากรรายนั้น
  const [selectedStaffUserName, setSelectedStaffUserName] = useState("ALL");

  // ข้อมูล Dashboard
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [allLeavesCache, setAllLeavesCache] = useState([]);

  // ข้อมูลสถิติรายบุคคล
  const [individualData, setIndividualData] = useState(null);
  const [loadingIndividual, setLoadingIndividual] = useState(false);

  // Search & Filter ในตารางบุคลากร (ภาพรวม)
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroupWork, setSelectedGroupWork] = useState("ALL");
  const [sortField, setSortField] = useState("totalDays");
  const [sortAsc, setSortAsc] = useState(false);

  // Modal สำหรับดูข้อมูลรายคนแบบป๊อปอัป
  const [selectedStaffForModal, setSelectedStaffForModal] = useState(null);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [modalIndividualData, setModalIndividualData] = useState(null);
  const [loadingModalData, setLoadingModalData] = useState(false);

  // =========================================================================
  // State สำหรับ TreeView และ User Cards (แบบเดียวกับ TSUser ในระบบวันลา / ห้องประชุม)
  // =========================================================================
  const [isUserSelectorOpen, setIsUserSelectorOpen] = useState(true);
  const [arrMission, setArrMission] = useState([]);
  const [treeUsers, setTreeUsers] = useState([]);
  const [treeKeyword, setTreeKeyword] = useState("");
  const [treeSelectedNodeTitle, setTreeSelectedNodeTitle] = useState("");
  const [loadingTreeUsers, setLoadingTreeUsers] = useState(false);

  // โหลดโครงสร้างภารกิจ/กลุ่มงาน/งาน สำหรับ TreeView
  useEffect(() => {
    axios
      .get("/api/v1/mission/")
      .then((res) => {
        if (res.data && res.data.result === 200 && Array.isArray(res.data.data)) {
          setArrMission(res.data.data);
        }
      })
      .catch((err) => {
        console.warn("Could not load /api/v1/mission/, using fallback structure", err);
      });
  }, []);

  // เมื่อคลิกโหนดใน TreeView (ภารกิจ M, กลุ่มงาน G, งาน/แผนก D)
  const handleClickTreeNode = (type, val, nodeName) => (e) => {
    if (e) e.stopPropagation();
    setTreeSelectedNodeTitle(nodeName || "");
    setLoadingTreeUsers(true);

    let param = "";
    if (type === "M") {
      param = "?Missionkey=" + val;
      if (!isDeputyOnly) setSelectedMission(parseInt(val, 10));
    } else if (type === "G") {
      param = "?GroupWorkKey=" + val;
    } else if (type === "D") {
      param = "?DepartmentKey=" + val;
    }

    axios
      .get("/api/v1/userview/" + param)
      .then((res) => {
        if (res.data && res.data.result === 200 && Array.isArray(res.data.data)) {
          setTreeUsers(res.data.data);
        } else {
          fallbackTreeUsersFilter(type, val);
        }
      })
      .catch(() => {
        fallbackTreeUsersFilter(type, val);
      })
      .finally(() => {
        setLoadingTreeUsers(false);
      });
  };

  // ค้นหาเจ้าหน้าที่จากชื่อในกล่องค้นหาแบบ TSUser
  const handleSearchTreeKeyword = (keywordVal) => {
    setTreeKeyword(keywordVal);
    if (!keywordVal.trim()) {
      setTreeUsers([]);
      return;
    }

    setLoadingTreeUsers(true);
    axios
      .get("/api/v1/userview/?FirstName=" + encodeURIComponent(keywordVal.trim()))
      .then((res) => {
        if (res.data && res.data.result === 200 && Array.isArray(res.data.data)) {
          let list = res.data.data;
          if (isDeputyOnly && userDeputyInfo) {
            list = list.filter((u) => u.missionKey === userDeputyInfo.missionKey);
          }
          setTreeUsers(list);
        } else {
          fallbackSearchLocal(keywordVal);
        }
      })
      .catch(() => {
        fallbackSearchLocal(keywordVal);
      })
      .finally(() => {
        setLoadingTreeUsers(false);
      });
  };

  // Fallback กรองจาก staffList ในระบบเมื่อ API ไม่ตอบสนอง
  const fallbackTreeUsersFilter = (type, val) => {
    if (!dashboardData || !dashboardData.staffList) return;
    let list = [...dashboardData.staffList];
    if (type === "M") {
      list = list.filter((s) => s.missionKey === parseInt(val, 10));
    } else if (type === "G") {
      list = list.filter((s) => s.groupWorkKey === parseInt(val, 10) || s.groupWorkName === val);
    } else if (type === "D") {
      list = list.filter((s) => s.departmentKey === parseInt(val, 10) || s.departmentName === val);
    }
    setTreeUsers(list);
  };

  const fallbackSearchLocal = (keywordVal) => {
    if (!dashboardData || !dashboardData.staffList) return;
    const q = keywordVal.toLowerCase();
    let list = dashboardData.staffList.filter(
      (s) =>
        (s.fullName && s.fullName.toLowerCase().includes(q)) ||
        (s.userName && s.userName.toLowerCase().includes(q))
    );
    if (isDeputyOnly && userDeputyInfo) {
      list = list.filter((s) => s.missionKey === userDeputyInfo.missionKey);
    }
    setTreeUsers(list);
  };

  // คำนวณสถิติ 12 เดือนและประเภทการลาสำหรับบุคลากรคนเดียว
  const computeStaffStatsFromLeaves = useCallback((staffUser, leaves, calYear, sDate, eDate, isCustom) => {
    let yearStart;
    let yearEnd;
    if (isCustom && sDate && eDate) {
      yearStart = new Date(sDate);
      yearEnd = new Date(eDate);
      yearEnd.setHours(23, 59, 59, 999);
    } else {
      yearStart = new Date(calYear - 544, 9, 1);
      yearEnd = new Date(calYear - 543, 8, 30, 23, 59, 59);
    }

    const userLeaves = leaves.filter((l) => {
      if (l.isCancel) return false;
      const u = (l.userName || "").toLowerCase();
      if (u !== (staffUser || "").toLowerCase()) return false;
      const s = new Date(l.startDate);
      return s >= yearStart && s <= yearEnd;
    });

    const totalDays = userLeaves.reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0);
    const vacationDays = userLeaves.filter((l) => l.leaveTypeKey === 3).reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0);
    const sickDays = userLeaves.filter((l) => l.leaveTypeKey === 1).reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0);
    const personalDays = userLeaves.filter((l) => l.leaveTypeKey === 2).reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0);
    const otherDays = userLeaves.filter((l) => l.leaveTypeKey > 3).reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0);

    let monthlyStats = [];
    if (isCustom && sDate && eDate) {
      const cur = new Date(yearStart.getFullYear(), yearStart.getMonth(), 1);
      const endM = new Date(yearEnd.getFullYear(), yearEnd.getMonth(), 1);
      const standardThaiMonths = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
      let mIdx = 1;
      while (cur <= endM && mIdx <= 36) {
        const mNum = cur.getMonth() + 1;
        const mYear = cur.getFullYear();
        const mLeaves = userLeaves.filter((l) => {
          const d = new Date(l.startDate);
          return d.getMonth() + 1 === mNum && d.getFullYear() === mYear;
        });
        const mDays = mLeaves.reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0);
        monthlyStats.push({
          monthIndex: mIdx,
          monthNumber: mNum,
          monthName: standardThaiMonths[mNum - 1],
          displayYear: mYear + 543,
          sickDays: mLeaves.filter((l) => l.leaveTypeKey === 1).reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0),
          personalDays: mLeaves.filter((l) => l.leaveTypeKey === 2).reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0),
          vacationDays: mLeaves.filter((l) => l.leaveTypeKey === 3).reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0),
          otherDays: mLeaves.filter((l) => l.leaveTypeKey > 3).reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0),
          totalDays: mDays,
          requestsCount: mLeaves.length
        });
        cur.setMonth(cur.getMonth() + 1);
        mIdx++;
      }
    } else {
      monthlyStats = MONTH_NUMBERS.map((mNum, idx) => {
        const mYear = mNum >= 10 ? calYear - 544 : calYear - 543;
        const mLeaves = userLeaves.filter((l) => {
          const d = new Date(l.startDate);
          return d.getMonth() + 1 === mNum && d.getFullYear() === mYear;
        });
        const mDays = mLeaves.reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0);
        return {
          monthIndex: idx + 1,
          monthNumber: mNum,
          monthName: MONTH_NAMES_THAI[idx],
          displayYear: mNum >= 10 ? calYear - 1 : calYear,
          sickDays: mLeaves.filter((l) => l.leaveTypeKey === 1).reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0),
          personalDays: mLeaves.filter((l) => l.leaveTypeKey === 2).reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0),
          vacationDays: mLeaves.filter((l) => l.leaveTypeKey === 3).reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0),
          otherDays: mLeaves.filter((l) => l.leaveTypeKey > 3).reduce((acc, l) => acc + (Number(l.totalDate) || 0), 0),
          totalDays: mDays,
          requestsCount: mLeaves.length
        };
      });
    }

    const typeBreakdown = [
      {
        leaveTypeKey: 3,
        leaveTypeName: "ลาพักผ่อน",
        totalDays: vacationDays,
        requestsCount: userLeaves.filter((l) => l.leaveTypeKey === 3).length,
        percentage: totalDays > 0 ? Math.round((vacationDays / totalDays) * 100) : 0
      },
      {
        leaveTypeKey: 1,
        leaveTypeName: "ลาป่วย",
        totalDays: sickDays,
        requestsCount: userLeaves.filter((l) => l.leaveTypeKey === 1).length,
        percentage: totalDays > 0 ? Math.round((sickDays / totalDays) * 100) : 0
      },
      {
        leaveTypeKey: 2,
        leaveTypeName: "ลากิจส่วนตัว",
        totalDays: personalDays,
        requestsCount: userLeaves.filter((l) => l.leaveTypeKey === 2).length,
        percentage: totalDays > 0 ? Math.round((personalDays / totalDays) * 100) : 0
      },
      {
        leaveTypeKey: 4,
        leaveTypeName: "อื่นๆ",
        totalDays: otherDays,
        requestsCount: userLeaves.filter((l) => l.leaveTypeKey > 3).length,
        percentage: totalDays > 0 ? Math.round((otherDays / totalDays) * 100) : 0
      }
    ];

    const sortedHistory = [...userLeaves].sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

    return {
      totalDays,
      vacationDays,
      sickDays,
      personalDays,
      otherDays,
      requestsCount: userLeaves.length,
      monthlyStats,
      typeBreakdown,
      leaveHistory: sortedHistory
    };
  }, []);

  // Fetch ข้อมูลภาพรวม
  const fetchDashboardData = useCallback(() => {
    setLoading(true);
    const missionToFetch = isDeputyOnly && userDeputyInfo ? userDeputyInfo.missionKey : selectedMission;

    let url = `/api/v1/leave/mission-dashboard?missionKey=${missionToFetch}`;
    if (filterMode === "custom" && customStartDate && customEndDate) {
      url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
    } else {
      url += `&calYear=${selectedYear}`;
    }

    axios
      .get(url)
      .then((res) => {
        if (res.data && res.data.result === 200) {
          const rawStaff = Array.isArray(res.data.staffList) ? res.data.staffList : [];
          const seenUsers = new Set();
          const uniqueStaff = rawStaff.filter((s) => {
            const u = (s.userName || "").toLowerCase();
            if (!u || seenUsers.has(u)) return false;
            seenUsers.add(u);
            return true;
          });

          setDashboardData({
            ...res.data,
            staffList: uniqueStaff
          });
          setLoading(false);
        } else {
          fallbackClientCalculation(missionToFetch, selectedYear);
        }
      })
      .catch((err) => {
        console.warn("Backend mission-dashboard endpoint not yet available, using client fallback", err);
        fallbackClientCalculation(missionToFetch, selectedYear);
      });
  }, [isDeputyOnly, userDeputyInfo, selectedMission, filterMode, customStartDate, customEndDate, selectedYear]);

  // Fallback Client Calculation
  const fallbackClientCalculation = async (missionKey, calYear) => {
    try {
      let yearStart;
      let yearEnd;
      if (filterMode === "custom" && customStartDate && customEndDate) {
        yearStart = new Date(customStartDate);
        yearEnd = new Date(customEndDate);
        yearEnd.setHours(23, 59, 59, 999);
      } else {
        yearStart = new Date(calYear - 544, 9, 1);
        yearEnd = new Date(calYear - 543, 8, 30, 23, 59, 59);
      }

      const resLeaves = await axios.get("/api/v1/leave/header/");
      const allLeaves = Array.isArray(resLeaves.data) ? resLeaves.data : [];
      setAllLeavesCache(allLeaves);

      const filteredLeaves = allLeaves.filter((l) => {
        if (l.isCancel) return false;
        const s = new Date(l.startDate);
        return s >= yearStart && s <= yearEnd;
      });

      const staffMap = {};
      filteredLeaves.forEach((l) => {
        const u = (l.userName || "").toLowerCase();
        if (!staffMap[u]) {
          staffMap[u] = {
            userName: l.userName,
            fullName: l.title ? l.title.split(" ")[1] + " " + (l.title.split(" ")[2] || "") : l.userName,
            positionName: "บุคลากร",
            departmentName: "สังกัดภารกิจ",
            groupWorkName: "กลุ่มงาน",
            missionKey: missionKey || 1,
            missionName: ALL_MISSIONS.find((m) => m.key === missionKey)?.name || "ภารกิจ",
            sickDays: 0,
            personalDays: 0,
            vacationDays: 0,
            otherDays: 0,
            totalDays: 0,
            requestsCount: 0,
            accruedTotal: 10,
            remainingVacation: 10
          };
        }
        const days = Number(l.totalDate) || 1;
        staffMap[u].totalDays += days;
        staffMap[u].requestsCount += 1;
        if (l.leaveTypeKey === 1) staffMap[u].sickDays += days;
        else if (l.leaveTypeKey === 2) staffMap[u].personalDays += days;
        else if (l.leaveTypeKey === 3) staffMap[u].vacationDays += days;
        else staffMap[u].otherDays += days;
        staffMap[u].remainingVacation = Math.max(0, staffMap[u].accruedTotal - staffMap[u].vacationDays);
      });

      const staffList = Object.values(staffMap).sort((a, b) => b.totalDays - a.totalDays);
      const totalDays = staffList.reduce((acc, s) => acc + s.totalDays, 0);
      const sickDays = staffList.reduce((acc, s) => acc + s.sickDays, 0);
      const personalDays = staffList.reduce((acc, s) => acc + s.personalDays, 0);
      const vacationDays = staffList.reduce((acc, s) => acc + s.vacationDays, 0);
      const otherDays = staffList.reduce((acc, s) => acc + s.otherDays, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayLeaves = filteredLeaves
        .filter((l) => {
          const s = new Date(l.startDate);
          const e = new Date(l.endDate);
          s.setHours(0, 0, 0, 0);
          e.setHours(0, 0, 0, 0);
          return s <= today && e >= today;
        })
        .map((l) => ({
          leaveRequestID: l.id || l.leaveRequestID,
          userName: l.userName,
          fullName: l.title || l.userName,
          positionName: "บุคลากร",
          departmentName: "สังกัดภารกิจ",
          leaveTypeName: l.leaveTypeKey === 1 ? "ลาป่วย" : l.leaveTypeKey === 2 ? "ลากิจ" : l.leaveTypeKey === 3 ? "ลาพักผ่อน" : "ลาอื่นๆ",
          startDate: l.startDate,
          endDate: l.endDate,
          totalDate: l.totalDate
        }));

      setDashboardData({
        result: 200,
        calYear,
        missionKey,
        missionName: ALL_MISSIONS.find((m) => m.key === missionKey)?.name || "ภารกิจ",
        kpi: {
          totalStaff: staffList.length,
          totalStaffOnLeave: staffList.length,
          totalRequests: filteredLeaves.length,
          approvedRequests: filteredLeaves.length,
          pendingRequests: 0,
          totalDays,
          sickDays,
          personalDays,
          vacationDays,
          otherDays
        },
        todayLeaves,
        staffList
      });
    } catch (e) {
      console.error("Fallback calculation error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // เมื่อเลือกบุคลากรรายบุคคล
  useEffect(() => {
    if (selectedStaffUserName === "ALL") {
      setIndividualData(null);
      return;
    }

    setLoadingIndividual(true);
    let url = `/api/v1/leave/mission-dashboard?userName=${encodeURIComponent(selectedStaffUserName)}`;
    if (filterMode === "custom" && customStartDate && customEndDate) {
      url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
    } else {
      url += `&calYear=${selectedYear}`;
    }

    axios
      .get(url)
      .then((res) => {
        if (res.data && res.data.result === 200) {
          setIndividualData({
            ...res.data.kpi,
            monthlyStats: res.data.monthlyStats,
            typeBreakdown: res.data.typeBreakdown,
            kpi: res.data.kpi,
            leaveHistory: res.data.leaveHistory || []
          });
          setLoadingIndividual(false);
        } else {
          fallbackIndividualCalculation(selectedStaffUserName);
        }
      })
      .catch(() => {
        fallbackIndividualCalculation(selectedStaffUserName);
      });
  }, [selectedStaffUserName, selectedYear, filterMode, customStartDate, customEndDate]);

  // Fallback คำนวณข้อมูลสถิติของคนๆ นั้นบน Client
  const fallbackIndividualCalculation = async (uName) => {
    try {
      let leaves = allLeavesCache;
      if (!leaves || leaves.length === 0) {
        const res = await axios.get("/api/v1/leave/header/");
        leaves = Array.isArray(res.data) ? res.data : [];
        setAllLeavesCache(leaves);
      }
      const stats = computeStaffStatsFromLeaves(uName, leaves, selectedYear, customStartDate, customEndDate, filterMode === "custom");
      setIndividualData(stats);
    } catch (err) {
      console.error("Error computing individual stats fallback", err);
    } finally {
      setLoadingIndividual(false);
    }
  };

  // เปิด Modal ดูข้อมูลรายคน
  const handleOpenStaffModal = async (staff) => {
    setSelectedStaffForModal(staff);
    setStaffModalOpen(true);
    setLoadingModalData(true);

    let url = `/api/v1/leave/mission-dashboard?userName=${encodeURIComponent(staff.userName)}`;
    if (filterMode === "custom" && customStartDate && customEndDate) {
      url += `&startDate=${customStartDate}&endDate=${customEndDate}`;
    } else {
      url += `&calYear=${selectedYear}`;
    }

    try {
      const res = await axios.get(url);
      if (res.data && res.data.result === 200) {
        setModalIndividualData({
          ...res.data.kpi,
          monthlyStats: res.data.monthlyStats,
          typeBreakdown: res.data.typeBreakdown,
          kpi: res.data.kpi,
          leaveHistory: res.data.leaveHistory || []
        });
      } else {
        const stats = computeStaffStatsFromLeaves(staff.userName, allLeavesCache, selectedYear, customStartDate, customEndDate, filterMode === "custom");
        setModalIndividualData(stats);
      }
    } catch (e) {
      const stats = computeStaffStatsFromLeaves(staff.userName, allLeavesCache, selectedYear, customStartDate, customEndDate, filterMode === "custom");
      setModalIndividualData(stats);
    } finally {
      setLoadingModalData(false);
    }
  };

  // ฟังก์ชันเลื่อนคอนเทนเนอร์แบบนุ่มนวล (รองรับ PerfectScrollbar, overflow: hidden, และ native)
  const smoothScrollToY = (container, targetY, duration = 350) => {
    if (!container) return;
    if (activeScrollRaf) {
      cancelAnimationFrame(activeScrollRaf);
      activeScrollRaf = null;
    }

    const startY = container.scrollTop || 0;
    const diff = targetY - startY;

    if (Math.abs(diff) < 2) {
      container.scrollTop = targetY;
      try { container.dispatchEvent(new Event("scroll")); } catch (e) {}
      return;
    }

    const startTime = performance.now();

    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic: 1 - Math.pow(1 - progress, 3)
      const ease = 1 - Math.pow(1 - progress, 3);
      const nextY = Math.round(startY + diff * ease);

      container.scrollTop = nextY;
      try { container.dispatchEvent(new Event("scroll")); } catch (e) {}

      if (progress < 1) {
        activeScrollRaf = requestAnimationFrame(step);
      } else {
        container.scrollTop = targetY;
        try { container.dispatchEvent(new Event("scroll")); } catch (e) {}
        activeScrollRaf = null;
      }
    };

    activeScrollRaf = requestAnimationFrame(step);
  };

  // ฟังก์ชันเลื่อนหน้าจอกลับขึ้นไปดูกราฟสถิติรายบุคคลอย่างนุ่มนวล
  const scrollToIndividualSection = () => {
    let attempts = 0;
    const maxAttempts = 20;

    const tryScroll = () => {
      attempts++;
      const targetEl = document.getElementById("individual-stats-view");
      const psEl = document.querySelector(".ps");
      const mainPanel =
        (targetEl ? targetEl.closest(".ps") : null) ||
        psEl ||
        (targetEl ? targetEl.closest("[class*='mainPanel']") : null) ||
        document.querySelector("[class*='mainPanel']") ||
        document.querySelector(".main-panel") ||
        document.getElementById("main-panel");

      if (targetEl && mainPanel) {
        const targetRect = targetEl.getBoundingClientRect();
        const panelRect = mainPanel.getBoundingClientRect();
        const targetTop = Math.max(0, targetRect.top - panelRect.top + mainPanel.scrollTop - 20);
        smoothScrollToY(mainPanel, targetTop, 350);

        const docEl = document.scrollingElement || document.documentElement || document.body;
        if (docEl && docEl.scrollTop > 0) {
          const docTargetTop = Math.max(0, targetRect.top + (window.pageYOffset || docEl.scrollTop) - 20);
          smoothScrollToY(docEl, docTargetTop, 350);
        }
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(tryScroll, 30);
      } else if (mainPanel) {
        smoothScrollToY(mainPanel, 0, 350);
      }
    };

    tryScroll();
  };

  // ฟังก์ชันเลื่อนหน้าจอกลับขึ้นสู่ภาพรวมภารกิจ
  const scrollToOverviewSection = () => {
    const psEl = document.querySelector(".ps");
    const mainPanel =
      psEl ||
      document.querySelector("[class*='mainPanel']") ||
      document.querySelector(".main-panel") ||
      document.getElementById("main-panel");

    if (mainPanel) {
      smoothScrollToY(mainPanel, 0, 350);
    }

    const docEl = document.scrollingElement || document.documentElement || document.body;
    if (docEl && docEl.scrollTop > 0) {
      smoothScrollToY(docEl, 0, 350);
    }

    if (typeof window.scrollTo === "function") {
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (e) {
        window.scrollTo(0, 0);
      }
    }
  };

  // เลือกบุคลากรเข้าสู่โหมดรายบุคคลบนหน้าจอหลัก
  const handleSelectStaffForMainView = (staff) => {
    const uName = typeof staff === "string" ? staff : staff.userName;
    setSelectedStaffUserName(uName);
    setStaffModalOpen(false);
    scrollToIndividualSection();
  };

  // กลับสู่ภาพรวมภารกิจ
  const handleReturnToOverview = () => {
    setSelectedStaffUserName("ALL");
    scrollToOverviewSection();
  };

  // เมื่อเลือกดูบุคลากรรายบุคคล ให้เลื่อนหน้าจอกลับขึ้นไปดูกราฟสถิติโดยอัตโนมัติ
  useEffect(() => {
    if (selectedStaffUserName !== "ALL") {
      scrollToIndividualSection();
    }
  }, [selectedStaffUserName]);

  // บุคลากรที่กำลังเลือกอยู่ในโหมดรายบุคคล
  const currentSelectedStaff = useMemo(() => {
    if (selectedStaffUserName === "ALL") return null;
    if (dashboardData && dashboardData.staffList) {
      const found = dashboardData.staffList.find((s) => (s.userName || "").toLowerCase() === selectedStaffUserName.toLowerCase());
      if (found) return found;
    }
    if (treeUsers && treeUsers.length > 0) {
      const foundInTree = treeUsers.find((s) => (s.userName || "").toLowerCase() === selectedStaffUserName.toLowerCase());
      if (foundInTree) {
        return {
          userName: foundInTree.userName,
          fullName: `${foundInTree.titleName || ""}${foundInTree.firstName || ""} ${foundInTree.lastName || ""}`.trim() || foundInTree.userName,
          positionName: foundInTree.positionName || "บุคลากร",
          departmentName: foundInTree.departmentName || "สังกัด",
          groupWorkName: foundInTree.groupWorkName || "",
          missionKey: foundInTree.missionKey || 0,
          missionName: foundInTree.missionName || "",
          phone: foundInTree.mobilePhone || foundInTree.phone || "",
          accruedTotal: 10,
          remainingVacation: 10,
          totalDays: individualData?.totalDays || individualData?.kpi?.totalDays || 0,
          vacationDays: individualData?.vacationDays || individualData?.kpi?.vacationDays || 0,
          sickDays: individualData?.sickDays || individualData?.kpi?.sickDays || 0,
          personalDays: individualData?.personalDays || individualData?.kpi?.personalDays || 0,
          otherDays: individualData?.otherDays || individualData?.kpi?.otherDays || 0,
          requestsCount: individualData?.requestsCount || individualData?.kpi?.totalRequests || 0
        };
      }
    }
    return null;
  }, [dashboardData, selectedStaffUserName, treeUsers, individualData]);

  // กรองและเรียงลำดับตารางรายชื่อบุคลากร (สำหรับหน้าภาพรวม): ตัดรายชื่อที่ซ้ำออก (Deduplicate)
  const filteredStaffList = useMemo(() => {
    if (!dashboardData || !dashboardData.staffList) return [];

    // กำจัดรายชื่อที่ซ้ำซ้อนกัน โดยยึด userName ที่ไม่ซ้ำเป็นหลัก
    const seenUsers = new Set();
    let list = [];
    for (const s of dashboardData.staffList) {
      const u = (s.userName || "").toLowerCase();
      if (!u || seenUsers.has(u)) continue;
      seenUsers.add(u);
      list.push(s);
    }

    if (selectedGroupWork !== "ALL") {
      list = list.filter((s) => s.groupWorkName === selectedGroupWork || s.departmentName === selectedGroupWork);
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (s) =>
          (s.fullName && s.fullName.toLowerCase().includes(q)) ||
          (s.userName && s.userName.toLowerCase().includes(q)) ||
          (s.positionName && s.positionName.toLowerCase().includes(q)) ||
          (s.departmentName && s.departmentName.toLowerCase().includes(q))
      );
    }

    list.sort((a, b) => {
      let valA = a[sortField] || 0;
      let valB = b[sortField] || 0;
      if (typeof valA === "string") {
        return sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      return sortAsc ? valA - valB : valB - valA;
    });

    return list;
  }, [dashboardData, selectedGroupWork, searchTerm, sortField, sortAsc]);

  // รายการกลุ่มงานสำหรับ Filter Dropdown ในตาราง
  const groupWorkOptions = useMemo(() => {
    if (!dashboardData || !dashboardData.staffList) return [];
    const set = new Set();
    dashboardData.staffList.forEach((s) => {
      if (s.groupWorkName) set.add(s.groupWorkName);
      else if (s.departmentName) set.add(s.departmentName);
    });
    return Array.from(set);
  }, [dashboardData]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  // รายการภารกิจสำหรับแสดงใน TreeView (หากเป็นรองภารกิจ แสดงเฉพาะภารกิจตนเอง)
  const visibleMissionsInTree = useMemo(() => {
    if (isDeputyOnly && userDeputyInfo) {
      return arrMission.filter((m) => m.missionKey === userDeputyInfo.missionKey);
    }
    return arrMission;
  }, [arrMission, isDeputyOnly, userDeputyInfo]);

  // Color Theme
  const activeMissionConfig =
    ALL_MISSIONS.find((m) => m.key === (isDeputyOnly && userDeputyInfo ? userDeputyInfo.missionKey : selectedMission)) ||
    ALL_MISSIONS[0];
  const themeColor = activeMissionConfig.color || "#0284c7";

  // ฟังก์ชันเรนเดอร์กราฟแท่ง 12 เดือน (เฉพาะเมื่อเลือกคนๆ นั้น): แสดงประเภทการลาแยกสีชัดเจน
  const renderMonthlyTrendChart = (monthlyStats, personName) => {
    const stats = monthlyStats || [];
    const maxVal = Math.max(1, ...stats.map((x) => x.totalDays || x.TotalDays || 0));

    // รวมสถิติประเภทการลาทั้งปีสำหรับ Legend
    const totalVacation = stats.reduce((acc, m) => acc + (m.vacationDays ?? m.VacationDays ?? 0), 0);
    const totalSick = stats.reduce((acc, m) => acc + (m.sickDays ?? m.SickDays ?? 0), 0);
    const totalPersonal = stats.reduce((acc, m) => acc + (m.personalDays ?? m.PersonalDays ?? 0), 0);
    const totalOther = stats.reduce((acc, m) => acc + (m.otherDays ?? m.OtherDays ?? 0), 0);
    const grandTotal = stats.reduce((acc, m) => acc + (m.totalDays ?? m.TotalDays ?? 0), 0);

    return (
      <Card style={{ marginBottom: "20px", borderRadius: "14px", boxShadow: "0 4px 15px -1px rgba(0,0,0,0.07), 0 2px 6px -2px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <CardHeader style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", color: "#ffffff", padding: "14px 20px" }}>
          <div style={{ fontWeight: "bold", fontSize: "1rem", letterSpacing: "0.2px" }}>
            📈 สถิติการลารายเดือน {filterMode === "fiscal" ? `ประจำปีงบประมาณ พ.ศ. ${selectedYear} (12 เดือน ต.ค. - ก.ย.)` : `ช่วงวันที่ ${formatThaiDateRange(customStartDate, customEndDate)}`}
          </div>
          {personName && (
            <div style={{ fontSize: "0.85rem", color: "#cbd5e1", marginTop: "3px" }}>
              แสดงข้อมูลเฉพาะบุคคล: <strong style={{ color: "#ffffff" }}>{personName}</strong>
            </div>
          )}
        </CardHeader>
        <CardBody style={{ padding: "20px" }}>
          {/* พื้นที่กราฟแท่งแบบ Stacked Bar แยกสีประเภทการลา */}
          <div
            className="d-flex justify-content-between align-items-end"
            style={{ height: "210px", paddingTop: "25px", borderBottom: "2px solid #cbd5e1", paddingBottom: "10px" }}
          >
            {stats.map((m, idx) => {
              const vDays = m.vacationDays ?? m.VacationDays ?? 0;
              const sDays = m.sickDays ?? m.SickDays ?? 0;
              const pDays = m.personalDays ?? m.PersonalDays ?? 0;
              const oDays = m.otherDays ?? m.OtherDays ?? 0;
              const days = m.totalDays ?? m.TotalDays ?? (vDays + sDays + pDays + oDays);
              const heightPct = Math.min(100, Math.round((days / maxVal) * 100));
              const hasLeave = days > 0;

              // รายละเอียดสำหรับ Tooltip
              const tooltipText = `${m.monthName} พ.ศ. ${m.displayYear}\nรวม: ${days} วัน\n` +
                (vDays > 0 ? `• 🏖️ ลาพักผ่อน: ${vDays} วัน\n` : "") +
                (sDays > 0 ? `• 🏥 ลาป่วย: ${sDays} วัน\n` : "") +
                (pDays > 0 ? `• 💼 ลากิจส่วนตัว: ${pDays} วัน\n` : "") +
                (oDays > 0 ? `• 📁 ลาอื่นๆ: ${oDays} วัน\n` : "") +
                (days === 0 ? "• ไม่มีการลาในเดือนนี้" : "");

              return (
                <div
                  key={idx}
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    height: "100%",
                    justifyContent: "flex-end",
                    margin: "0 3px"
                  }}
                >
                  {/* ตัวเลขรวมวันลาเหนือแท่งกราฟ */}
                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: hasLeave ? "#000000" : "transparent",
                      fontWeight: "bold",
                      marginBottom: "6px"
                    }}
                  >
                    {hasLeave ? days : "0"}
                  </div>

                  {/* แท่งกราฟ Stacked Bar แยกสีประเภทการลา */}
                  <div
                    style={{
                      width: "82%",
                      maxWidth: "42px",
                      height: `${Math.max(8, heightPct)}%`,
                      borderRadius: "6px 6px 0 0",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column-reverse", // เรียงจากล่างขึ้นบน: พักผ่อน (ล่าง) -> ลากิจ -> ลาป่วย -> อื่นๆ (บน)
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                      boxShadow: hasLeave ? "0 4px 10px rgba(0,0,0,0.14)" : "none",
                      border: hasLeave ? "1px solid rgba(0,0,0,0.08)" : "1px dashed #cbd5e1",
                      borderBottom: "none",
                      backgroundColor: hasLeave ? "transparent" : "#f8fafc"
                    }}
                    title={tooltipText}
                  >
                    {/* 1. ลาพักผ่อน: สีเขียวมรกต (#10b981) */}
                    {vDays > 0 && (
                      <div
                        style={{
                          height: `${(vDays / days) * 100}%`,
                          backgroundColor: "#10b981",
                          width: "100%",
                          transition: "height 0.3s ease"
                        }}
                        title={`🏖️ ลาพักผ่อน: ${vDays} วัน`}
                      />
                    )}

                    {/* 2. ลากิจส่วนตัว: สีส้มอำพัน (#f59e0b) */}
                    {pDays > 0 && (
                      <div
                        style={{
                          height: `${(pDays / days) * 100}%`,
                          backgroundColor: "#f59e0b",
                          width: "100%",
                          transition: "height 0.3s ease"
                        }}
                        title={`💼 ลากิจส่วนตัว: ${pDays} วัน`}
                      />
                    )}

                    {/* 3. ลาป่วย: สีแดง (#ef4444) */}
                    {sDays > 0 && (
                      <div
                        style={{
                          height: `${(sDays / days) * 100}%`,
                          backgroundColor: "#ef4444",
                          width: "100%",
                          transition: "height 0.3s ease"
                        }}
                        title={`🏥 ลาป่วย: ${sDays} วัน`}
                      />
                    )}

                    {/* 4. ลาอื่นๆ: สีม่วง (#8b5cf6) */}
                    {oDays > 0 && (
                      <div
                        style={{
                          height: `${(oDays / days) * 100}%`,
                          backgroundColor: "#8b5cf6",
                          width: "100%",
                          transition: "height 0.3s ease"
                        }}
                        title={`📁 ลาอื่นๆ: ${oDays} วัน`}
                      />
                    )}
                  </div>

                  {/* ชื่อเดือนใต้แท่งกราฟ */}
                  <div
                    style={{
                      fontSize: "0.82rem",
                      color: "#000000",
                      marginTop: "10px",
                      fontWeight: "bold"
                    }}
                  >
                    {m.monthName}
                  </div>
                </div>
              );
            })}
          </div>

          {/* แถบคำอธิบายสีและประเภทการลาทั้งหมด (Legend with Totals) */}
          <div
            className="d-flex flex-wrap justify-content-center align-items-center gap-2 mt-3 pt-3"
            style={{ fontSize: "0.85rem", color: "#000000", fontWeight: "bold", borderTop: "1px solid #f1f5f9" }}
          >
            {/* 1. ลาพักผ่อน */}
            <span style={{ display: "inline-flex", alignItems: "center", backgroundColor: "#ecfdf5", padding: "6px 12px", borderRadius: "20px", border: "1.5px solid #10b981" }}>
              <span
                style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  backgroundColor: "#10b981",
                  marginRight: "7px",
                  borderRadius: "3px"
                }}
              />
              <span style={{ color: "#000000" }}>🏖️ ลาพักผ่อน:</span>
              <strong style={{ color: "#047857", marginLeft: "5px" }}>{totalVacation} วัน</strong>
            </span>

            {/* 2. ลาป่วย */}
            <span style={{ display: "inline-flex", alignItems: "center", backgroundColor: "#fef2f2", padding: "6px 12px", borderRadius: "20px", border: "1.5px solid #ef4444" }}>
              <span
                style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  backgroundColor: "#ef4444",
                  marginRight: "7px",
                  borderRadius: "3px"
                }}
              />
              <span style={{ color: "#000000" }}>🏥 ลาป่วย:</span>
              <strong style={{ color: "#b91c1c", marginLeft: "5px" }}>{totalSick} วัน</strong>
            </span>

            {/* 3. ลากิจส่วนตัว */}
            <span style={{ display: "inline-flex", alignItems: "center", backgroundColor: "#fffbeb", padding: "6px 12px", borderRadius: "20px", border: "1.5px solid #f59e0b" }}>
              <span
                style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  backgroundColor: "#f59e0b",
                  marginRight: "7px",
                  borderRadius: "3px"
                }}
              />
              <span style={{ color: "#000000" }}>💼 ลากิจส่วนตัว:</span>
              <strong style={{ color: "#b45309", marginLeft: "5px" }}>{totalPersonal} วัน</strong>
            </span>

            {/* 4. ลาอื่นๆ (แสดงเมื่อมียอดลาอื่นๆ) */}
            {totalOther > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", backgroundColor: "#f5f3ff", padding: "6px 12px", borderRadius: "20px", border: "1.5px solid #8b5cf6" }}>
                <span
                  style={{
                    display: "inline-block",
                    width: "12px",
                    height: "12px",
                    backgroundColor: "#8b5cf6",
                    marginRight: "7px",
                    borderRadius: "3px"
                  }}
                />
                <span style={{ color: "#000000" }}>📁 ลาอื่นๆ:</span>
                <strong style={{ color: "#6d28d9", marginLeft: "5px" }}>{totalOther} วัน</strong>
              </span>
            )}

            {/* 5. รวมวันลาทั้งหมด */}
            <span style={{ display: "inline-flex", alignItems: "center", backgroundColor: "#f1f5f9", padding: "6px 14px", borderRadius: "20px", border: "1.5px solid #cbd5e1" }}>
              <span style={{ color: "#000000" }}>⏱️ รวมทั้งปี:</span>
              <strong style={{ color: "#0f172a", marginLeft: "6px" }}>{grandTotal} วัน</strong>
            </span>
          </div>
        </CardBody>
      </Card>
    );
  };

  // ฟังก์ชันเรนเดอร์สัดส่วนประเภทการลา (Donut/Progress breakdown)
  const renderLeaveTypeBreakdown = (typeBreakdown, personName) => {
    const list = typeBreakdown || [];

    return (
      <Card style={{ marginBottom: "20px", borderRadius: "14px", boxShadow: "0 4px 15px -1px rgba(0,0,0,0.07), 0 2px 6px -2px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <CardHeader style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", color: "#ffffff", padding: "14px 20px" }}>
          <div style={{ fontWeight: "bold", fontSize: "1rem", letterSpacing: "0.2px" }}>
            🍩 สัดส่วนประเภทการลา {filterMode === "custom" ? `(ช่วง ${formatThaiDateRange(customStartDate, customEndDate)})` : `(ปีงบประมาณ พ.ศ. ${selectedYear})`}
          </div>
          {personName && (
            <div style={{ fontSize: "0.85rem", color: "#cbd5e1", marginTop: "3px" }}>
              ของ: <strong style={{ color: "#ffffff" }}>{personName}</strong>
            </div>
          )}
        </CardHeader>
        <CardBody style={{ padding: "20px" }}>
          {list.length > 0 ? (
            list.map((tb, idx) => {
              let barColor = "#0f172a";
              let badgeBg = "#f1f5f9";
              if (tb.leaveTypeKey === 3) { barColor = "#10b981"; badgeBg = "#ecfdf5"; }
              else if (tb.leaveTypeKey === 1) { barColor = "#ef4444"; badgeBg = "#fef2f2"; }
              else if (tb.leaveTypeKey === 2) { barColor = "#f59e0b"; badgeBg = "#fffbeb"; }
              else if (tb.leaveTypeKey > 3) { barColor = "#8b5cf6"; badgeBg = "#f5f3ff"; }

              return (
                <div key={idx} style={{ marginBottom: "18px" }}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span style={{ fontSize: "0.92rem", fontWeight: "bold", color: "#000000" }}>
                      {tb.leaveTypeName}
                    </span>
                    <span
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                        color: barColor,
                        backgroundColor: badgeBg,
                        padding: "2px 8px",
                        borderRadius: "6px",
                        border: `1px solid ${barColor}30`
                      }}
                    >
                      {tb.totalDays} วัน ({tb.percentage}%)
                    </span>
                  </div>
                  <div
                    style={{
                      height: "10px",
                      backgroundColor: "#f1f5f9",
                      borderRadius: "6px",
                      overflow: "hidden",
                      border: "1px solid #e2e8f0"
                    }}
                  >
                    <div
                      style={{
                        width: `${tb.percentage}%`,
                        height: "100%",
                        backgroundColor: barColor,
                        borderRadius: "6px",
                        transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
                      }}
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ textAlign: "center", color: "#000000", fontWeight: "bold", padding: "30px 20px" }}>
              ไม่มีข้อมูลประเภทการลา
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  // ฟังก์ชันเรนเดอร์กราฟโดนัทมินิ สัดส่วนประเภทการลา (แสดงแทนบล็อกสิทธิพักผ่อน)
  const renderMiniLeaveDonut = (staffStats) => {
    const vDays = staffStats?.vacationDays ?? 0;
    const sDays = staffStats?.sickDays ?? 0;
    const pDays = staffStats?.personalDays ?? 0;
    const oDays = staffStats?.otherDays ?? 0;
    const total = staffStats?.totalDays ?? (vDays + sDays + pDays + oDays);

    const radius = 35;
    const circumference = 2 * Math.PI * radius; // ~219.91

    const items = [
      { key: "vacation", name: "ลาพักผ่อน", days: vDays, color: "#10b981", icon: "🏖️" },
      { key: "sick", name: "ลาป่วย", days: sDays, color: "#ef4444", icon: "🏥" },
      { key: "personal", name: "ลากิจ", days: pDays, color: "#f59e0b", icon: "💼" },
      ...(oDays > 0 ? [{ key: "other", name: "ลาอื่นๆ", days: oDays, color: "#8b5cf6", icon: "📁" }] : [])
    ];

    let cumulativePercent = 0;

    return (
      <div
        style={{
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          padding: "12px 16px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
        }}
      >
        <div style={{ fontSize: "0.88rem", fontWeight: "bold", color: "#000000", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
          <span>🍩</span>
          <span>สัดส่วนประเภทการลา ({filterMode === "custom" ? `ช่วง ${formatThaiDateRange(customStartDate, customEndDate)}` : `ปีงบประมาณ พ.ศ. ${selectedYear}`})</span>
        </div>

        <div className="d-flex align-items-center justify-content-between" style={{ gap: "14px" }}>
          {/* Donut Chart SVG */}
          <div style={{ width: "96px", height: "96px", flexShrink: 0, position: "relative" }}>
            <svg viewBox="0 0 100 100" width="100%" height="100%">
              {/* Background ring */}
              <circle
                cx="50"
                cy="50"
                r={radius}
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="13"
              />

              {/* Segments */}
              {total > 0 && items.map((item) => {
                if (item.days <= 0) return null;
                const strokeLength = (item.days / total) * circumference;
                const strokeOffset = -((cumulativePercent / 100) * circumference);
                cumulativePercent += (item.days / total) * 100;

                return (
                  <circle
                    key={item.key}
                    cx="50"
                    cy="50"
                    r={radius}
                    fill="none"
                    stroke={item.color}
                    strokeWidth="13"
                    strokeDasharray={`${strokeLength} ${circumference}`}
                    strokeDashoffset={strokeOffset}
                    transform="rotate(-90 50 50)"
                    style={{ transition: "stroke-dasharray 0.4s ease" }}
                  />
                );
              })}

              {/* Center hole text */}
              <text x="50" y="47" textAnchor="middle" fontSize="13" fontWeight="800" fill="#000000">
                {total}
              </text>
              <text x="50" y="60" textAnchor="middle" fontSize="6.8" fontWeight="bold" fill="#475569">
                วันรวม
              </text>
            </svg>
          </div>

          {/* Breakdown labels on the right */}
          <div style={{ flex: 1 }}>
            {items.map((item) => {
              const pct = total > 0 ? Math.round((item.days / total) * 100) : 0;
              return (
                <div
                  key={item.key}
                  className="d-flex align-items-center justify-content-between mb-1"
                  style={{ fontSize: "0.82rem", lineHeight: "1.25" }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: item.color,
                        display: "inline-block"
                      }}
                    />
                    <span style={{ color: "#000000", fontWeight: "600" }}>{item.icon} {item.name}:</span>
                  </span>
                  <span style={{ fontWeight: "800", color: item.color }}>
                    {item.days} วัน <span style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: "500" }}>({pct}%)</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: '"Sarabun", sans-serif', paddingBottom: "35px" }}>
      {/* 1. Header Banner: Modern Executive Dashboard Style */}
      <Card style={{ marginBottom: "20px", borderRadius: "14px", boxShadow: "0 4px 15px -1px rgba(0,0,0,0.07), 0 2px 6px -2px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", position: "relative", zIndex: 60, overflow: "visible" }}>
        <CardHeader
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            color: "#ffffff",
            padding: "18px 24px",
            borderRadius: "14px 14px 0 0",
            borderBottom: "1px solid rgba(255,255,255,0.08)"
          }}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "1.6rem" }}>📊</span>
                <h4 style={{ margin: 0, fontSize: "1.35rem", fontWeight: "800", color: "#ffffff", letterSpacing: "-0.01em" }}>
                  Dashboard สถิติการลาประจำภารกิจ
                </h4>
              </div>
              <p style={{ margin: "4px 0 0 0", color: "#94a3b8", fontSize: "0.88rem" }}>
                ระบบรายงานภาพรวมและสถิติการลาของบุคลากรภายในภารกิจ สำหรับผู้บริหารสายบัญชาการ
              </p>
            </div>
            <div className="mt-2 mt-md-0 d-flex align-items-center gap-2">
              {isDeputyOnly && userDeputyInfo ? (
                <div style={{ textAlign: "right" }}>
                  <span
                    style={{
                      backgroundColor: "rgba(255,255,255,0.12)",
                      color: "#ffffff",
                      fontSize: "0.88rem",
                      padding: "6px 14px",
                      borderRadius: "20px",
                      fontWeight: "bold",
                      border: "1px solid rgba(255,255,255,0.2)",
                      display: "inline-block"
                    }}
                  >
                    ⭐ รองภารกิจ: {userDeputyInfo.deputyTitle}
                  </span>
                  <div style={{ fontSize: "0.78rem", color: "#cbd5e1", marginTop: "4px" }}>
                    {userDeputyInfo.missionName}
                  </div>
                </div>
              ) : isIT ? (
                <span
                  style={{
                    backgroundColor: "rgba(16, 185, 129, 0.2)",
                    color: "#6ee7b7",
                    fontSize: "0.85rem",
                    padding: "6px 16px",
                    borderRadius: "20px",
                    fontWeight: "bold",
                    border: "1px solid rgba(16, 185, 129, 0.4)"
                  }}
                >
                  💻 สิทธิ์งานคอมพิวเตอร์ (ดูได้ทุกภารกิจ)
                </span>
              ) : isHR ? (
                <span
                  style={{
                    backgroundColor: "rgba(14, 165, 233, 0.2)",
                    color: "#7dd3fc",
                    fontSize: "0.85rem",
                    padding: "6px 16px",
                    borderRadius: "20px",
                    fontWeight: "bold",
                    border: "1px solid rgba(14, 165, 233, 0.4)"
                  }}
                >
                  👥 สิทธิ์งานทรัพยากรบุคคล (HR) (ดูได้ทุกภารกิจ)
                </span>
              ) : (
                <span
                  style={{
                    backgroundColor: "rgba(245, 158, 11, 0.2)",
                    color: "#fcd34d",
                    fontSize: "0.85rem",
                    padding: "6px 16px",
                    borderRadius: "20px",
                    fontWeight: "bold",
                    border: "1px solid rgba(245, 158, 11, 0.4)"
                  }}
                >
                  🛠️ สิทธิ์ผู้ดูแลระบบ (ดูได้ทุกภารกิจ)
                </span>
              )}
            </div>
          </div>
        </CardHeader>

        {/* 2. Filter Controls Bar: "เลือกปีงบประมาณ" หรือ "กำหนดช่วงวันที่เอง (ปฏิทิน พ.ศ.)" */}
        <CardBody style={{ padding: "20px 24px", backgroundColor: "#ffffff", borderBottom: "1px solid #f1f5f9", borderRadius: "0 0 14px 14px", position: "relative", overflow: "visible" }}>
          {/* สไตล์สำหรับปฏิทิน popup react-datetime และ TreeView ให้ตัวหนังสือดำชัดเจนและสวยงามโมเดิร์น */}
          <style>{`
            .rdt {
              position: relative !important;
            }
            .rdtPicker {
              position: absolute !important;
              top: 100% !important;
              left: 0 !important;
              margin-top: 6px !important;
              background-color: #ffffff !important;
              border: 1.5px solid #cbd5e1 !important;
              border-radius: 12px !important;
              box-shadow: 0 15px 35px -5px rgba(0,0,0,0.25) !important;
              z-index: 99999 !important;
              padding: 12px !important;
              font-family: "Sarabun", sans-serif !important;
              min-width: 290px !important;
            }
            .rdtPicker th, .rdtPicker td {
              color: #000000 !important;
              font-weight: 600 !important;
              font-size: 0.95rem !important;
              text-align: center !important;
            }
            .rdtPicker th.rdtSwitch {
              font-size: 1.05rem !important;
              font-weight: bold !important;
              color: #000000 !important;
              cursor: pointer !important;
            }
            .rdtPicker th.rdtNext, .rdtPicker th.rdtPrev {
              font-size: 1.3rem !important;
              font-weight: bold !important;
              color: #000000 !important;
              cursor: pointer !important;
            }
            .rdtPicker .rdtDay.rdtActive, .rdtPicker .rdtMonth.rdtActive, .rdtPicker .rdtYear.rdtActive {
              background-color: #0f172a !important;
              color: #ffffff !important;
              border-radius: 8px !important;
              font-weight: bold !important;
            }
            .rdtPicker .rdtDay:hover, .rdtPicker .rdtMonth:hover, .rdtPicker .rdtYear:hover {
              background-color: #f1f5f9 !important;
              color: #000000 !important;
              border-radius: 8px !important;
            }
            .rdtPicker .rdtOld, .rdtPicker .rdtNew {
              color: #64748b !important;
              font-weight: normal !important;
            }
            .rdtPicker .rdtToday:before {
              border-bottom: 7px solid #0f172a !important;
            }

            /* TreeView overrides ให้ตัวหนังสือและไอคอนในโครงสร้างสายบัญชาการเป็นสีดำเข้ม ชัดเจน อ่านง่าย */
            .MuiTreeItem-root {
              color: #000000 !important;
            }
            .MuiTreeItem-content {
              color: #000000 !important;
              border-radius: 8px !important;
              padding: 4px 8px !important;
              transition: all 0.15s ease !important;
            }
            .MuiTreeItem-content:hover {
              background-color: #f1f5f9 !important;
            }
            .MuiTreeItem-label {
              color: #000000 !important;
              font-weight: 600 !important;
              font-size: 0.94rem !important;
            }
            .MuiTreeItem-root.Mui-selected > .MuiTreeItem-content {
              background-color: #e0f2fe !important;
              border-left: 3px solid #0284c7 !important;
            }
            .MuiTreeItem-root.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label {
              color: #000000 !important;
              font-weight: bold !important;
            }
            .MuiTreeItem-iconContainer svg {
              color: #000000 !important;
              font-size: 1.35rem !important;
            }

            /* บังคับตัวหนังสือในตารางและหัวตารางทั้งหมดเป็นสีดำเข้ม ชัดเจน อ่านง่าย */
            .table th {
              color: #000000 !important;
              font-weight: 700 !important;
              font-size: 0.92rem !important;
              border-bottom: 2px solid #e2e8f0 !important;
              background-color: #f8fafc !important;
              padding: 12px 14px !important;
            }
            .table td {
              color: #000000 !important;
              font-weight: 500 !important;
              font-size: 0.9rem !important;
              padding: 12px 14px !important;
              vertical-align: middle !important;
            }
            .table-striped tbody tr:nth-of-type(odd) {
              background-color: rgba(248, 250, 252, 0.6) !important;
            }
            .table-hover tbody tr:hover {
              background-color: #f1f5f9 !important;
            }

            /* Placeholder ให้สีเทาเข้มอ่านออกง่าย ไม่จาง */
            ::placeholder {
              color: #64748b !important;
              font-weight: 500 !important;
              opacity: 1 !important;
            }
          `}</style>

          {/* แถบสลับโหมดตัวกรอง: ตามปีงบประมาณ vs กำหนดช่วงวันที่เอง (Modern Segmented Pill) */}
          <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 pb-3" style={{ borderBottom: "1px solid #f1f5f9" }}>
            <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
              <span style={{ fontSize: "0.95rem", fontWeight: "bold", color: "#000000", marginRight: "8px" }}>
                🎯 รูปแบบการกรองข้อมูล:
              </span>
              <div style={{ backgroundColor: "#f1f5f9", padding: "4px", borderRadius: "10px", display: "inline-flex", border: "1px solid #e2e8f0" }}>
                <button
                  type="button"
                  onClick={() => setFilterMode("fiscal")}
                  style={{
                    borderRadius: "7px",
                    fontSize: "0.88rem",
                    padding: "7px 18px",
                    fontWeight: "bold",
                    backgroundColor: filterMode === "fiscal" ? "#0f172a" : "transparent",
                    color: filterMode === "fiscal" ? "#ffffff" : "#000000",
                    border: "none",
                    boxShadow: filterMode === "fiscal" ? "0 2px 5px rgba(0,0,0,0.12)" : "none",
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                >
                  📅 ตามปีงบประมาณ (พ.ศ.)
                </button>
                <button
                  type="button"
                  onClick={() => setFilterMode("custom")}
                  style={{
                    borderRadius: "7px",
                    fontSize: "0.88rem",
                    padding: "7px 18px",
                    fontWeight: "bold",
                    backgroundColor: filterMode === "custom" ? "#0f172a" : "transparent",
                    color: filterMode === "custom" ? "#ffffff" : "#000000",
                    border: "none",
                    boxShadow: filterMode === "custom" ? "0 2px 5px rgba(0,0,0,0.12)" : "none",
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                >
                  🗓️ กำหนดช่วงวันที่เอง (ปฏิทิน พ.ศ.)
                </button>
              </div>
            </div>

            {/* แสดงช่วงวันที่ที่กำลังแสดงผลเป็น พ.ศ. ชัดเจน หรูหราทันสมัย พร้อมปุ่มเคลียร์ค่า */}
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  backgroundColor: filterMode === "custom" ? "#eff6ff" : "#f8fafc",
                  color: "#000000",
                  border: filterMode === "custom" ? "1.5px solid #3b82f6" : "1px solid #cbd5e1",
                  borderRadius: "8px",
                  fontSize: "0.92rem",
                  fontWeight: "bold",
                  padding: "7px 16px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px"
                }}
              >
                <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: filterMode === "custom" ? "#2563eb" : "#16a34a" }} />
                {filterMode === "fiscal"
                  ? `ข้อมูลประจำปีงบประมาณ พ.ศ. ${selectedYear} (1 ต.ค. ${selectedYear - 1} - 30 ก.ย. ${selectedYear})`
                  : `ข้อมูลช่วงวันที่: ${formatThaiDateRange(customStartDate, customEndDate)}`}
              </div>
              {filterMode === "custom" && (
                <button
                  type="button"
                  onClick={handleClearFilter}
                  style={{
                    backgroundColor: "#fef2f2",
                    color: "#dc2626",
                    border: "1.5px solid #fecaca",
                    borderRadius: "8px",
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                    padding: "7px 14px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)"
                  }}
                  title="เคลียร์ช่วงวันที่ และกลับสู่ปีงบประมาณ พ.ศ. ปัจจุบัน"
                >
                  ❌ เคลียร์ค่า
                </button>
              )}
            </div>
          </div>

          <div className="row g-3 align-items-center" style={{ position: "relative", zIndex: 65, overflow: "visible" }}>
            {filterMode === "fiscal" ? (
              /* เลือกปีงบประมาณ (พ.ศ.) */
              <div className="col-lg-5 col-md-6 col-sm-12 mb-2">
                <Label style={{ fontSize: "0.95rem", fontWeight: "bold", color: "#000000", marginBottom: "6px", display: "block" }}>
                  📅 เลือกปีงบประมาณ (พ.ศ.)
                </Label>
                <Input
                  type="select"
                  className="form-control"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                  style={{
                    fontWeight: "bold",
                    color: "#000000",
                    backgroundColor: "#ffffff",
                    border: "1.5px solid #cbd5e1",
                    borderRadius: "8px",
                    height: "44px",
                    fontSize: "0.98rem",
                    cursor: "pointer",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)"
                  }}
                >
                  {yearOptions.map((y) => (
                    <option key={y.value} value={y.value} style={{ color: "#000000", fontWeight: "bold" }}>
                      {y.label}
                    </option>
                  ))}
                </Input>
              </div>
            ) : (
              /* กำหนดช่วงวันที่เอง (ปฏิทิน พ.ศ.) */
              <div className="col-lg-7 col-md-8 col-sm-12 mb-2" style={{ position: "relative", zIndex: 70, overflow: "visible" }}>
                <div className="row g-2" style={{ position: "relative", zIndex: 75, overflow: "visible" }}>
                  <div className="col-sm-6" style={{ position: "relative", zIndex: 90, overflow: "visible" }}>
                    <Label style={{ fontSize: "0.95rem", fontWeight: "bold", color: "#000000", marginBottom: "6px", display: "block" }}>
                      📅 วันที่เริ่มต้น (พ.ศ.)
                    </Label>
                    <div style={{ position: "relative" }}>
                      <Datetime
                        value={customStartDate && moment(customStartDate).isValid() ? moment(customStartDate) : null}
                        renderYear={(props, year, selectedDate) => <td {...props}>{year + 543}</td>}
                        inputProps={{
                          placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                          readOnly: "readonly",
                          style: {
                            cursor: "pointer",
                            backgroundColor: "#ffffff",
                            color: "#000000",
                            fontWeight: "bold",
                            fontSize: "1rem",
                            border: "1.5px solid #cbd5e1",
                            borderRadius: "8px",
                            height: "44px",
                            paddingRight: "38px",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)"
                          },
                          className: "form-control",
                          value:
                            customStartDate && moment(customStartDate).isValid()
                              ? moment(customStartDate).format("D MMMM BBBB")
                              : ""
                        }}
                        closeOnSelect={true}
                        closeOnTab={true}
                        dateFormat="D MMMM BBBB"
                        timeFormat={false}
                        onChange={(val) => {
                          if (moment.isMoment(val) && val.isValid()) {
                            setCustomStartDate(val.format("YYYY-MM-DD"));
                          } else if (val && typeof val === "string") {
                            const m = moment(val);
                            if (m.isValid()) setCustomStartDate(m.format("YYYY-MM-DD"));
                          } else if (!val) {
                            setCustomStartDate("");
                          }
                        }}
                      />
                      <i
                        className="far fa-calendar-alt"
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "13px",
                          color: "#0284c7",
                          fontSize: "1.15rem",
                          pointerEvents: "none"
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6" style={{ position: "relative", zIndex: 85, overflow: "visible" }}>
                    <Label style={{ fontSize: "0.95rem", fontWeight: "bold", color: "#000000", marginBottom: "6px", display: "block" }}>
                      📅 วันที่สิ้นสุด (พ.ศ.)
                    </Label>
                    <div style={{ position: "relative" }}>
                      <Datetime
                        value={customEndDate && moment(customEndDate).isValid() ? moment(customEndDate) : null}
                        renderYear={(props, year, selectedDate) => <td {...props}>{year + 543}</td>}
                        inputProps={{
                          placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                          readOnly: "readonly",
                          style: {
                            cursor: "pointer",
                            backgroundColor: "#ffffff",
                            color: "#000000",
                            fontWeight: "bold",
                            fontSize: "1rem",
                            border: "1.5px solid #cbd5e1",
                            borderRadius: "8px",
                            height: "44px",
                            paddingRight: "38px",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)"
                          },
                          className: "form-control",
                          value:
                            customEndDate && moment(customEndDate).isValid()
                              ? moment(customEndDate).format("D MMMM BBBB")
                              : ""
                        }}
                        closeOnSelect={true}
                        closeOnTab={true}
                        dateFormat="D MMMM BBBB"
                        timeFormat={false}
                        onChange={(val) => {
                          if (moment.isMoment(val) && val.isValid()) {
                            setCustomEndDate(val.format("YYYY-MM-DD"));
                          } else if (val && typeof val === "string") {
                            const m = moment(val);
                            if (m.isValid()) setCustomEndDate(m.format("YYYY-MM-DD"));
                          } else if (!val) {
                            setCustomEndDate("");
                          }
                        }}
                      />
                      <i
                        className="far fa-calendar-alt"
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "13px",
                          color: "#0284c7",
                          fontSize: "1.15rem",
                          pointerEvents: "none"
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* ทางลัดช่วงวันที่ - สไตล์ชิปแคปซูลโมเดิร์น */}
                <div className="d-flex flex-wrap align-items-center gap-2 mt-2 pt-1">
                  <span style={{ fontSize: "0.88rem", fontWeight: "bold", color: "#000000" }}>ทางลัด:</span>
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{
                      backgroundColor: "#f8fafc",
                      color: "#000000",
                      border: "1px solid #cbd5e1",
                      fontWeight: "600",
                      fontSize: "0.84rem",
                      borderRadius: "20px",
                      padding: "4px 14px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                      cursor: "pointer",
                      transition: "all 0.15s ease"
                    }}
                    onClick={() => {
                      setCustomStartDate(`${currentCalYear - 544}-10-01`);
                      setCustomEndDate(`${currentCalYear - 543}-09-30`);
                    }}
                  >
                    ปีงบปัจจุบัน (พ.ศ. {currentCalYear})
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{
                      backgroundColor: "#f8fafc",
                      color: "#000000",
                      border: "1px solid #cbd5e1",
                      fontWeight: "600",
                      fontSize: "0.84rem",
                      borderRadius: "20px",
                      padding: "4px 14px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                      cursor: "pointer",
                      transition: "all 0.15s ease"
                    }}
                    onClick={() => {
                      setCustomStartDate(moment().startOf("month").format("YYYY-MM-DD"));
                      setCustomEndDate(moment().endOf("month").format("YYYY-MM-DD"));
                    }}
                  >
                    เดือนนี้
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{
                      backgroundColor: "#f8fafc",
                      color: "#000000",
                      border: "1px solid #cbd5e1",
                      fontWeight: "600",
                      fontSize: "0.84rem",
                      borderRadius: "20px",
                      padding: "4px 14px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                      cursor: "pointer",
                      transition: "all 0.15s ease"
                    }}
                    onClick={() => {
                      setCustomStartDate(moment().subtract(3, "months").format("YYYY-MM-DD"));
                      setCustomEndDate(moment().format("YYYY-MM-DD"));
                    }}
                  >
                    3 เดือนย้อนหลัง
                  </button>
                </div>
              </div>
            )}

            {/* ปุ่มดำเนินการด้านขวา: Modern Gradient Buttons */}
            <div className={`${filterMode === "fiscal" ? "col-lg-7 col-md-6" : "col-lg-5 col-md-4"} col-sm-12 mb-2 d-flex flex-wrap justify-content-lg-end align-items-center gap-2`}>
              {selectedStaffUserName !== "ALL" && (
                <Button
                  color="secondary"
                  size="sm"
                  onClick={handleReturnToOverview}
                  style={{
                    fontWeight: "bold",
                    padding: "9px 18px",
                    borderRadius: "8px",
                    backgroundColor: "#f8fafc",
                    color: "#0f172a",
                    border: "1.5px solid #cbd5e1",
                    marginTop: filterMode === "fiscal" ? "24px" : "0px",
                    cursor: "pointer",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)"
                  }}
                >
                  👥 กลับสู่ภาพรวมภารกิจ
                </Button>
              )}
              {filterMode === "custom" && (
                <Button
                  color="danger"
                  size="sm"
                  onClick={handleClearFilter}
                  style={{
                    fontWeight: "bold",
                    padding: "10px 18px",
                    borderRadius: "8px",
                    backgroundColor: "#fef2f2",
                    color: "#b91c1c",
                    border: "1.5px solid #fecaca",
                    cursor: "pointer",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    transition: "all 0.2s ease",
                    fontSize: "0.9rem"
                  }}
                  title="เคลียร์ช่วงวันที่ และกลับสู่ปีงบประมาณ พ.ศ. ปัจจุบัน"
                >
                  ❌ เคลียร์ค่าตัวกรอง
                </Button>
              )}
              <Button
                color="primary"
                size="sm"
                onClick={fetchDashboardData}
                disabled={loading}
                style={{
                  fontWeight: "bold",
                  padding: "10px 22px",
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                  border: "none",
                  color: "#ffffff",
                  marginTop: filterMode === "fiscal" ? "24px" : "0px",
                  minWidth: "150px",
                  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.25)",
                  cursor: "pointer",
                  fontSize: "0.92rem",
                  transition: "all 0.2s ease"
                }}
              >
                {loading ? "⏳ กำลังโหลด..." : "🔄 กรอง / รีเฟรชข้อมูล"}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ========================================================================= */}
      {/* 3. แผงค้นหาและเลือกคนแบบระบบวันลา และระบบจองห้องประชุม (TreeView + User Cards) */}
      {/* ========================================================================= */}
      <Card style={{ marginBottom: "20px", borderRadius: "14px", boxShadow: "0 4px 15px -1px rgba(0,0,0,0.07), 0 2px 6px -2px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", position: "relative", zIndex: 10, overflow: "hidden" }}>
        <CardHeader
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            color: "#ffffff",
            padding: "16px 24px",
            borderRadius: "14px 14px 0 0",
            borderBottom: "1px solid rgba(255,255,255,0.08)"
          }}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <div style={{ fontWeight: "800", fontSize: "1.1rem", display: "flex", alignItems: "center", gap: "8px" }}>
                <span>👥</span> ค้นหาและเลือกดูบุคลากร (โครงสร้างภารกิจ / สายงาน)
              </div>
              <div style={{ fontSize: "0.82rem", color: "#94a3b8", marginTop: "3px" }}>
                คลิกเลือกกลุ่มงานหรือแผนกจากโครงสร้างทางซ้าย หรือพิมพ์ค้นหาชื่อทางขวา แล้วคลิกที่รูปภาพเพื่อดูสถิติรายบุคคล
              </div>
            </div>
            <div className="mt-2 mt-md-0 d-flex gap-2 align-items-center">
              {selectedStaffUserName !== "ALL" && (
                <span style={{ backgroundColor: "#166534", color: "#ffffff", fontSize: "0.85rem", padding: "6px 14px", borderRadius: "20px", fontWeight: "bold" }}>
                  👤 กำลังดู: {currentSelectedStaff?.fullName || selectedStaffUserName}
                </span>
              )}
              <button
                type="button"
                onClick={() => setIsUserSelectorOpen(!isUserSelectorOpen)}
                style={{
                  fontSize: "0.85rem",
                  fontWeight: "bold",
                  padding: "6px 16px",
                  borderRadius: "8px",
                  backgroundColor: "rgba(255,255,255,0.12)",
                  color: "#ffffff",
                  border: "1px solid rgba(255,255,255,0.2)",
                  cursor: "pointer",
                  transition: "all 0.2s ease"
                }}
              >
                {isUserSelectorOpen ? "🔼 ซ่อนแผงเลือกคน" : "🔽 แสดงแผงเลือกคน"}
              </button>
            </div>
          </div>
        </CardHeader>

        {isUserSelectorOpen && (
          <CardBody style={{ padding: "18px", backgroundColor: "#f8fafc" }}>
            <Grid container spacing={2}>
              {/* คอลัมน์ซ้าย: โครงสร้าง TreeView (ภารกิจ -> กลุ่มงาน -> แผนก) */}
              <Col xs={12} sm={12} md={4} lg={4}>
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "16px",
                    height: "100%",
                    minHeight: "340px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.95rem",
                      fontWeight: "bold",
                      color: "#000000",
                      borderBottom: "1.5px solid #f1f5f9",
                      paddingBottom: "10px",
                      marginBottom: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}
                  >
                    <span>📂 โครงสร้างสายบัญชาการ</span>
                    <span
                      style={{
                        color: "#0f172a",
                        backgroundColor: "#f1f5f9",
                        border: "1px solid #e2e8f0",
                        borderRadius: "12px",
                        fontSize: "0.78rem",
                        fontWeight: "bold",
                        padding: "3px 10px"
                      }}
                    >
                      คลิกเพื่อเลือกแผนก
                    </span>
                  </div>

                  <TreeView
                    className={classes.treeRoot}
                    defaultCollapseIcon={<ArrowDropDownIcon style={{ color: "#000000", fontSize: "1.4rem" }} />}
                    defaultExpandIcon={<ArrowRightIcon style={{ color: "#000000", fontSize: "1.4rem" }} />}
                    defaultExpanded={
                      isDeputyOnly && userDeputyInfo
                        ? [`M${userDeputyInfo.missionKey}`]
                        : ["M1", "M2", "M3", "M5"]
                    }
                  >
                    {visibleMissionsInTree.map((mission) => (
                      <StyledTreeItem
                        key={`M${mission.missionKey}`}
                        nodeId={`M${mission.missionKey}`}
                        labelText={mission.name}
                        labelIcon={BusinessIcon}
                        color="#0284c7"
                        onTreeClick={handleClickTreeNode("M", mission.missionKey, mission.name)}
                      >
                        {Array.isArray(mission.groupWork) &&
                          mission.groupWork.map((group) => (
                            <StyledTreeItem
                              key={`G${group.groupWorkKey}`}
                              nodeId={`G${group.groupWorkKey}`}
                              labelText={group.name}
                              labelIcon={FolderIcon}
                              color="#7c3aed"
                              onTreeClick={handleClickTreeNode("G", group.groupWorkKey, `${mission.name} > ${group.name}`)}
                            >
                              {Array.isArray(group.department) &&
                                group.department.map((dept) => (
                                  <StyledTreeItem
                                    key={`D${dept.departmentKey}`}
                                    nodeId={`D${dept.departmentKey}`}
                                    labelText={dept.name}
                                    labelIcon={PeopleIcon}
                                    color="#059669"
                                    onTreeClick={handleClickTreeNode(
                                      "D",
                                      dept.departmentKey,
                                      `${group.name} > ${dept.name}`
                                    )}
                                  />
                                ))}
                            </StyledTreeItem>
                          ))}
                      </StyledTreeItem>
                    ))}
                  </TreeView>
                </div>
              </Col>

              {/* คอลัมน์ขวา: ค้นหาชื่อ และแสดงการ์ดรูปภาพเจ้าหน้าที่ (Avatar Cards) */}
              <Col xs={12} sm={12} md={8} lg={8}>
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "16px",
                    height: "100%",
                    minHeight: "340px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
                  }}
                >
                  {/* แถบค้นหาชื่อเจ้าหน้าที่ */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div style={{ flex: 1, minWidth: "220px" }}>
                      <Input
                        type="text"
                        placeholder="🔍 พิมพ์ค้นหาชื่อ-นามสกุล..."
                        value={treeKeyword}
                        onChange={(e) => handleSearchTreeKeyword(e.target.value)}
                        style={{
                          height: "44px",
                          fontSize: "0.95rem",
                          fontWeight: "bold",
                          color: "#000000",
                          border: "1.5px solid #cbd5e1",
                          borderRadius: "8px",
                          backgroundColor: "#ffffff",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.03)"
                        }}
                      />
                    </div>
                    <div>
                      <Button
                        size="sm"
                        onClick={handleReturnToOverview}
                        style={{
                          fontWeight: "bold",
                          height: "44px",
                          padding: "0 18px",
                          borderRadius: "8px",
                          backgroundColor: selectedStaffUserName === "ALL" ? "#0f172a" : "#f8fafc",
                          color: selectedStaffUserName === "ALL" ? "#ffffff" : "#0f172a",
                          border: "1.5px solid #cbd5e1",
                          fontSize: "0.88rem",
                          cursor: "pointer",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                          transition: "all 0.2s ease"
                        }}
                      >
                        👥 ดูภาพรวม {isDeputyOnly ? "ภารกิจตนเอง" : "ทั้งหมด"}
                      </Button>
                    </div>
                  </div>

                  {/* ป้ายกำกับกลุ่มที่เลือกปัจจุบัน */}
                  {treeSelectedNodeTitle && (
                    <div
                      style={{
                        fontSize: "0.92rem",
                        fontWeight: "bold",
                        color: "#000000",
                        backgroundColor: "#f0f9ff",
                        border: "1px solid #bae6fd",
                        borderRadius: "8px",
                        padding: "9px 16px",
                        marginBottom: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                    >
                      <span style={{ fontSize: "1.1rem" }}>📍</span>
                      <span>กำลังแสดงบุคลากรใน: <strong>{treeSelectedNodeTitle}</strong> ({treeUsers.length} คน)</span>
                    </div>
                  )}

                  {/* รายการการ์ดรูปภาพเจ้าหน้าที่ (Avatar Card Grid) */}
                  <div style={{ maxHeight: "310px", overflowY: "auto", padding: "4px" }}>
                    {loadingTreeUsers ? (
                      <div style={{ textAlign: "center", padding: "30px", color: "#000000", fontWeight: "bold", fontSize: "0.95rem" }}>
                        ⏳ กำลังค้นหาข้อมูลเจ้าหน้าที่...
                      </div>
                    ) : treeUsers && treeUsers.length > 0 ? (
                      <div className="d-flex flex-wrap justify-content-start">
                        {treeUsers.map((usr) => {
                          const isSelected = selectedStaffUserName === usr.userName;
                          return (
                            <ButtonBase
                              key={usr.userName}
                              onClick={() => handleSelectStaffForMainView(usr)}
                              style={{ textAlign: "center" }}
                            >
                              <div className={isSelected ? classes.userCardSmSelected : classes.userCardSm}>
                                <img
                                  src={"https://e.trangskin.go.th/avatar/" + usr.userName + ".jpg"}
                                  alt={usr.fullName || usr.userName}
                                  className={classes.cardImg}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://ui-avatars.com/api/?background=0D8ABC&color=fff&name=" + encodeURIComponent(usr.firstName || usr.userName);
                                  }}
                                />
                                <div
                                  style={{
                                    fontSize: "0.88rem",
                                    fontWeight: "bold",
                                    color: isSelected ? "#166534" : "#000000",
                                    lineHeight: "1.25"
                                  }}
                                >
                                  {usr.firstName} {usr.lastName}
                                </div>
                                {usr.nickName && (
                                  <div style={{ fontSize: "0.8rem", fontWeight: "600", color: "#000000" }}>
                                    ({usr.nickName})
                                  </div>
                                )}
                                <div
                                  style={{
                                    fontSize: "0.76rem",
                                    fontWeight: "500",
                                    color: "#000000",
                                    marginTop: "2px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                  }}
                                >
                                  {usr.positionName || usr.departmentName}
                                </div>
                                {isSelected && (
                                  <Badge color="success" style={{ fontSize: "0.68rem", marginTop: "4px" }}>
                                    ✓ กำลังเลือก
                                  </Badge>
                                )}
                              </div>
                            </ButtonBase>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ textAlign: "center", padding: "40px 20px", color: "#000000", fontSize: "0.98rem", fontWeight: "bold" }}>
                        {treeKeyword ? (
                          <span>❌ ไม่พบเจ้าหน้าที่ตรงกับชื่อ "{treeKeyword}"</span>
                        ) : (
                          <span>👈 คลิกเลือกแผนก/กลุ่มงานทางด้านซ้าย หรือพิมพ์ค้นหาชื่อด้านบนเพื่อแสดงรายชื่อเจ้าหน้าที่</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Grid>
          </CardBody>
        )}
      </Card>

      {/* ========================================================================= */}
      {/* 4. CONDITIONAL VIEW: OVERVIEW MODE (เมื่อเลือก "ภาพรวมทั้งภารกิจ")           */}
      {/* กราฟแท่ง 12 เดือน และสัดส่วนประเภทการลา จะถูก "ซ่อนจากภาพรวม" ตามคำสั่งผู้ใช้    */}
      {/* ========================================================================= */}
      {selectedStaffUserName === "ALL" ? (
        <div>
          {/* ซ่อน KPI Cards ในภาพรวมตามคำสั่งผู้ใช้: "อันนี้ซ่อนไว้ก่อน โชว์เวลาเลือกคนเท่านั้น" */}

          {/* Widget: บุคลากรที่กำลังลาในวันนี้: Modern Clean Card */}
          <Card style={{ marginBottom: "20px", borderRadius: "14px", border: "1px solid #bbf7d0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", overflow: "hidden" }}>
            <CardBody style={{ padding: "16px 20px", backgroundColor: "#f0fdf4" }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div style={{ fontWeight: "800", color: "#166534", fontSize: "0.98rem", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span>🟢</span>
                  <span>บุคลากรที่กำลังลาในวันนี้ ({moment().format("D MMMM")} {moment().year() + 543}):</span>
                  <Badge color="success" pill style={{ fontSize: "0.82rem", fontWeight: "bold", padding: "4px 10px" }}>
                    {dashboardData?.todayLeaves?.length || 0} คน
                  </Badge>
                </div>
              </div>
              {dashboardData?.todayLeaves && dashboardData.todayLeaves.length > 0 ? (
                <div className="row g-2">
                  {dashboardData.todayLeaves.map((tl, idx) => (
                    <div key={idx} className="col-md-4 col-sm-6 mb-2">
                      <div
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #bbf7d0",
                          borderRadius: "10px",
                          padding: "12px 16px",
                          fontSize: "0.88rem",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
                        }}
                      >
                        <div style={{ fontWeight: "bold", color: "#000000" }}>👤 {tl.fullName}</div>
                        <div style={{ fontSize: "0.8rem", color: "#334155", fontWeight: "500", marginTop: "2px" }}>
                          {tl.positionName} ({tl.departmentName})
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <Badge color="primary" style={{ fontSize: "0.75rem", fontWeight: "bold", padding: "4px 8px", borderRadius: "6px" }}>
                            {tl.leaveTypeName} ({tl.totalDate} วัน)
                          </Badge>
                          <span style={{ fontSize: "0.8rem", color: "#000000", fontWeight: "bold" }}>
                            📅 {formatThaiDateRange(tl.startDate, tl.endDate)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: "0.92rem", color: "#166534", fontWeight: "bold" }}>
                  ✨ วันนี้ไม่มีบุคลากรที่อยู่ในระหว่างการลา (ทุกคนพร้อมปฏิบัติงาน)
                </div>
              )}
            </CardBody>
          </Card>

          {/* ตารางสรุปรายชื่อบุคลากรและสถิติวันลา: Modern Executive Table */}
          <Card style={{ borderRadius: "14px", boxShadow: "0 4px 15px -1px rgba(0,0,0,0.07), 0 2px 6px -2px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <CardHeader
              style={{
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                color: "#ffffff",
                padding: "16px 24px",
                borderRadius: "14px 14px 0 0",
                borderBottom: "1px solid rgba(255,255,255,0.08)"
              }}
            >
              <div className="d-flex flex-wrap justify-content-between align-items-center">
                <div>
                  <h5 style={{ margin: 0, fontWeight: "800", fontSize: "1.1rem", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span>📋</span> รายชื่อบุคลากรและสถิติวันลา ({filteredStaffList.length} คน)
                    <span style={{ fontSize: "0.85rem", fontWeight: "600", color: "#93c5fd" }}>
                      {filterMode === "custom" ? `[ช่วง ${formatThaiDateRange(customStartDate, customEndDate)}]` : `[ปีงบประมาณ พ.ศ. ${selectedYear}]`}
                    </span>
                  </h5>
                  <div style={{ fontSize: "0.82rem", color: "#94a3b8", marginTop: "3px" }}>
                    คลิกที่รูปภาพในแผงค้นหาด้านบน หรือกดปุ่ม &quot;ดูกราฟสถิติ&quot; ในตาราง เพื่อเลือกดูข้อมูลเฉพาะบุคคล
                  </div>
                </div>
                <div className="mt-2 mt-md-0 d-flex gap-2">
                  <Input
                    type="text"
                    placeholder="🔍 ค้นหาชื่อ-สกุล / ตำแหน่ง..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      height: "40px",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      width: "240px",
                      backgroundColor: "#ffffff",
                      color: "#000000",
                      border: "1.5px solid #cbd5e1",
                      borderRadius: "8px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.03)"
                    }}
                  />
                  {groupWorkOptions.length > 0 && (
                    <Input
                      type="select"
                      value={selectedGroupWork}
                      onChange={(e) => setSelectedGroupWork(e.target.value)}
                      style={{
                        height: "40px",
                        fontSize: "0.9rem",
                        fontWeight: "bold",
                        width: "200px",
                        backgroundColor: "#ffffff",
                        color: "#000000",
                        border: "1.5px solid #cbd5e1",
                        borderRadius: "8px",
                        cursor: "pointer",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.03)"
                      }}
                    >
                      <option value="ALL">📁 ทุกกลุ่มงาน/งาน</option>
                      {groupWorkOptions.map((gw, idx) => (
                        <option key={idx} value={gw} style={{ color: "#000000", fontWeight: "bold" }}>
                          {gw}
                        </option>
                      ))}
                    </Input>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardBody style={{ padding: 0 }}>
              <div className="table-responsive">
                <Table hover striped style={{ margin: 0, fontSize: "0.9rem" }}>
                  <thead style={{ backgroundColor: "#f8fafc", color: "#000000", borderBottom: "2px solid #e2e8f0" }}>
                    <tr>
                      <th style={{ width: "50px", textAlign: "center", color: "#000000" }}>#</th>
                      <th style={{ cursor: "pointer", color: "#000000" }} onClick={() => handleSort("fullName")}>
                        ชื่อ - นามสกุล {sortField === "fullName" ? (sortAsc ? "▲" : "▼") : ""}
                      </th>
                      <th style={{ color: "#000000" }}>ตำแหน่ง / สังกัด</th>
                      <th
                        style={{ textAlign: "center", cursor: "pointer", color: "#047857", fontWeight: "bold" }}
                        onClick={() => handleSort("vacationDays")}
                      >
                        ลาพักผ่อน {sortField === "vacationDays" ? (sortAsc ? "▲" : "▼") : ""}
                      </th>
                      <th
                        style={{ textAlign: "center", cursor: "pointer", color: "#b91c1c", fontWeight: "bold" }}
                        onClick={() => handleSort("sickDays")}
                      >
                        ลาป่วย {sortField === "sickDays" ? (sortAsc ? "▲" : "▼") : ""}
                      </th>
                      <th
                        style={{ textAlign: "center", cursor: "pointer", color: "#b45309", fontWeight: "bold" }}
                        onClick={() => handleSort("personalDays")}
                      >
                        ลากิจ {sortField === "personalDays" ? (sortAsc ? "▲" : "▼") : ""}
                      </th>
                      <th style={{ textAlign: "center", color: "#000000" }}>ลาอื่นๆ</th>
                      <th
                        style={{ textAlign: "center", cursor: "pointer", fontWeight: "bold", color: "#000000" }}
                        onClick={() => handleSort("totalDays")}
                      >
                        รวมวันลา {sortField === "totalDays" ? (sortAsc ? "▲" : "▼") : ""}
                      </th>
                      <th style={{ textAlign: "center", color: "#0369a1", fontWeight: "bold" }}>พักผ่อนคงเหลือ</th>
                      <th style={{ textAlign: "center", width: "160px", color: "#000000" }}>จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStaffList.length > 0 ? (
                      filteredStaffList.map((s, idx) => (
                        <tr key={idx} style={{ verticalAlign: "middle" }}>
                          <td style={{ textAlign: "center", color: "#000000", fontWeight: "bold" }}>{idx + 1}</td>
                          <td>
                            <div style={{ fontWeight: "bold", color: "#000000" }}>{s.fullName}</div>
                            <div style={{ fontSize: "0.78rem", color: "#334155", fontWeight: "500" }}>@{s.userName}</div>
                          </td>
                          <td>
                            <div style={{ color: "#000000", fontWeight: "600" }}>{s.positionName}</div>
                            <div style={{ fontSize: "0.78rem", color: "#334155", marginTop: "2px" }}>
                              {s.groupWorkName || s.departmentName}
                              {s.missionName && (
                                <span style={{ marginLeft: "6px", backgroundColor: "#f1f5f9", color: "#0f172a", border: "1px solid #cbd5e1", borderRadius: "10px", padding: "1px 8px", fontSize: "0.72rem", fontWeight: "bold" }}>
                                  {s.missionName}
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ textAlign: "center", fontWeight: "bold", color: "#047857" }}>
                            {s.vacationDays > 0 ? `${s.vacationDays} วัน` : "-"}
                          </td>
                          <td style={{ textAlign: "center", fontWeight: "bold", color: "#b91c1c" }}>
                            {s.sickDays > 0 ? `${s.sickDays} วัน` : "-"}
                          </td>
                          <td style={{ textAlign: "center", fontWeight: "bold", color: "#b45309" }}>
                            {s.personalDays > 0 ? `${s.personalDays} วัน` : "-"}
                          </td>
                          <td style={{ textAlign: "center", color: "#000000", fontWeight: "600" }}>
                            {s.otherDays > 0 ? `${s.otherDays} วัน` : "-"}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <Badge
                              color={s.totalDays > 15 ? "danger" : s.totalDays > 7 ? "warning" : "info"}
                              style={{ fontSize: "0.85rem", padding: "5px 10px", fontWeight: "bold", borderRadius: "6px" }}
                            >
                              {s.totalDays} วัน ({s.requestsCount} ครั้ง)
                            </Badge>
                          </td>
                          <td style={{ textAlign: "center", color: "#0369a1", fontWeight: "bold", fontSize: "0.95rem" }}>
                            {s.remainingVacation !== undefined ? `${s.remainingVacation} วัน` : "-"}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <div className="d-flex justify-content-center gap-1">
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => handleOpenStaffModal(s)}
                                title="ดูรายละเอียดประวัติการลา (ป๊อปอัป)"
                                style={{
                                  fontSize: "0.8rem",
                                  fontWeight: "bold",
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
                                }}
                              >
                                👁️ ดูรายคน
                              </Button>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => handleSelectStaffForMainView(s)}
                                title="เปิดกราฟสถิติรายเดือนของคนนี้"
                                style={{
                                  fontSize: "0.8rem",
                                  fontWeight: "bold",
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  marginLeft: "4px",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
                                }}
                              >
                                📊 สถิติ
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" style={{ textAlign: "center", padding: "28px", color: "#000000", fontWeight: "bold", fontSize: "1rem" }}>
                          {loading ? "⏳ กำลังโหลดข้อมูล..." : "❌ ไม่พบข้อมูลบุคลากรตามเงื่อนไขที่เลือก"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : (
        /* ========================================================================= */
        /* 5. CONDITIONAL VIEW: INDIVIDUAL STAFF MODE (เมื่อเลือกดูเฉพาะคนๆ นั้น)       */
        /* แสดงกราฟแท่ง 12 เดือน และสัดส่วนประเภทการลา "เฉพาะของคนๆ นั้น" ตามคำสั่ง        */
        /* ========================================================================= */
        <div id="individual-stats-view">
          {/* แถบแจ้งเตือนและปุ่มย้อนกลับสู่ภาพรวม */}
          <div
            className="d-flex flex-wrap justify-content-between align-items-center mb-3 p-3"
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderLeft: `5px solid ${themeColor}`,
              borderRadius: "14px",
              boxShadow: "0 4px 15px -1px rgba(0,0,0,0.06), 0 2px 6px -2px rgba(0,0,0,0.04)"
            }}
          >
            <div className="d-flex align-items-center gap-3">
              <img
                src={"https://e.trangskin.go.th/avatar/" + selectedStaffUserName + ".jpg"}
                alt={currentSelectedStaff?.fullName || selectedStaffUserName}
                style={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2.5px solid #0284c7",
                  boxShadow: "0 2px 8px rgba(2,132,199,0.25)"
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://ui-avatars.com/api/?background=0D8ABC&color=fff&name=" + encodeURIComponent(currentSelectedStaff?.fullName || selectedStaffUserName);
                }}
              />
              <div>
                <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "#000000", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span>👤 สถิติการลารายบุคคล: {currentSelectedStaff?.fullName || selectedStaffUserName}</span>
                </div>
                <div style={{ fontSize: "0.88rem", fontWeight: "600", color: "#000000", marginTop: "2px" }}>
                  {currentSelectedStaff?.positionName || "บุคลากร"} • {currentSelectedStaff?.departmentName}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span
                style={{
                  fontSize: "0.85rem",
                  fontWeight: "bold",
                  backgroundColor: filterMode === "custom" ? "#eff6ff" : "#f8fafc",
                  color: filterMode === "custom" ? "#1e40af" : "#0f172a",
                  border: filterMode === "custom" ? "1.5px solid #93c5fd" : "1px solid #cbd5e1",
                  borderRadius: "20px",
                  padding: "6px 14px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <span>{filterMode === "custom" ? "🗓️ ช่วงวันที่:" : "📅 ปีงบประมาณ:"}</span>
                <span>{filterMode === "custom" ? formatThaiDateRange(customStartDate, customEndDate) : `พ.ศ. ${selectedYear}`}</span>
                {filterMode === "custom" && (
                  <button
                    type="button"
                    onClick={handleClearFilter}
                    style={{ background: "none", border: "none", color: "#dc2626", fontWeight: "bold", cursor: "pointer", padding: "0 2px" }}
                    title="เคลียร์ช่วงวันที่ และกลับสู่ปีงบประมาณ พ.ศ. ปัจจุบัน"
                  >
                    ✖
                  </button>
                )}
              </span>
              <button
                type="button"
                onClick={handleReturnToOverview}
                style={{
                  fontWeight: "bold",
                  borderRadius: "20px",
                  padding: "8px 18px",
                  backgroundColor: "#f1f5f9",
                  color: "#0f172a",
                  border: "1.5px solid #cbd5e1",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "0.88rem",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#e2e8f0"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#f1f5f9"; }}
              >
                ⬅️ กลับสู่ภาพรวมภารกิจ
              </button>
            </div>
          </div>

          {/* สรุปโปรไฟล์และสิทธิการลาของบุคลากรรายนี้ */}
          {currentSelectedStaff && (
            <Card style={{ marginBottom: "20px", borderRadius: "14px", border: "1px solid #e2e8f0", boxShadow: "0 4px 15px -1px rgba(0,0,0,0.06), 0 2px 6px -2px rgba(0,0,0,0.04)", overflow: "hidden" }}>
              <CardBody style={{ padding: "20px 24px" }}>
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div style={{ fontSize: "1.25rem", fontWeight: "800", color: "#000000" }}>
                      {currentSelectedStaff.fullName}
                    </div>
                    <div style={{ color: "#000000", fontSize: "0.92rem", fontWeight: "600", marginTop: "4px" }}>
                      ตำแหน่ง: <strong style={{ color: "#000000" }}>{currentSelectedStaff.positionName || "-"}</strong>
                    </div>
                    <div style={{ color: "#000000", fontSize: "0.88rem", fontWeight: "500", marginTop: "2px" }}>
                      สังกัด: {currentSelectedStaff.departmentName} ({currentSelectedStaff.groupWorkName || "กลุ่มงาน"})
                    </div>
                    {currentSelectedStaff.phone && (
                      <div style={{ color: "#000000", fontSize: "0.88rem", fontWeight: "500", marginTop: "2px" }}>
                        📞 เบอร์โทรศัพท์: {currentSelectedStaff.phone}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6 mt-3 mt-md-0">
                    {renderMiniLeaveDonut({
                      vacationDays: individualData?.vacationDays ?? individualData?.kpi?.vacationDays ?? currentSelectedStaff.vacationDays ?? 0,
                      sickDays: individualData?.sickDays ?? individualData?.kpi?.sickDays ?? currentSelectedStaff.sickDays ?? 0,
                      personalDays: individualData?.personalDays ?? individualData?.kpi?.personalDays ?? currentSelectedStaff.personalDays ?? 0,
                      otherDays: individualData?.otherDays ?? individualData?.kpi?.otherDays ?? currentSelectedStaff.otherDays ?? 0,
                      totalDays: individualData?.totalDays ?? individualData?.kpi?.totalDays ?? currentSelectedStaff.totalDays ?? 0
                    })}
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* KPI Cards สรุปวันลาเฉพาะคนๆ นี้: Modern Executive Style */}
          <Grid container spacing={2}>
            <Col xs={12} sm={6} md={3}>
              <div
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderTop: "4px solid #059669",
                  borderRadius: "14px",
                  padding: "18px 20px",
                  boxShadow: "0 4px 12px -1px rgba(0,0,0,0.06), 0 2px 4px -2px rgba(0,0,0,0.04)",
                  marginBottom: "18px",
                  position: "relative"
                }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div style={{ fontSize: "0.88rem", color: "#000000", fontWeight: "bold" }}>รวมวันลาทุกประเภท</div>
                    <div style={{ fontSize: "1.85rem", fontWeight: "800", color: "#059669", marginTop: "4px", lineHeight: 1.2 }}>
                      {(individualData?.totalDays ?? individualData?.kpi?.totalDays ?? currentSelectedStaff?.totalDays ?? 0)}{" "}
                      <span style={{ fontSize: "0.95rem", fontWeight: "bold", color: "#000000" }}>วัน</span>
                    </div>
                  </div>
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "10px",
                      backgroundColor: "#ecfdf5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.25rem",
                      boxShadow: "0 2px 5px rgba(5,150,105,0.15)"
                    }}
                  >
                    ⏱️
                  </div>
                </div>
                <div style={{ fontSize: "0.82rem", color: "#000000", fontWeight: "600", marginTop: "10px", paddingTop: "8px", borderTop: "1px solid #f1f5f9" }}>
                  จำนวนคำขอลา:{" "}
                  <strong>{(individualData?.requestsCount ?? individualData?.kpi?.totalRequests ?? currentSelectedStaff?.requestsCount ?? 0)} ครั้ง</strong>
                </div>
              </div>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <div
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderTop: "4px solid #10b981",
                  borderRadius: "14px",
                  padding: "18px 20px",
                  boxShadow: "0 4px 12px -1px rgba(0,0,0,0.06), 0 2px 4px -2px rgba(0,0,0,0.04)",
                  marginBottom: "18px",
                  position: "relative"
                }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div style={{ fontSize: "0.88rem", color: "#000000", fontWeight: "bold" }}>ลาพักผ่อน</div>
                    <div style={{ fontSize: "1.85rem", fontWeight: "800", color: "#10b981", marginTop: "4px", lineHeight: 1.2 }}>
                      {(individualData?.vacationDays ?? individualData?.kpi?.vacationDays ?? currentSelectedStaff?.vacationDays ?? 0)}{" "}
                      <span style={{ fontSize: "0.95rem", fontWeight: "bold", color: "#000000" }}>วัน</span>
                    </div>
                  </div>
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "10px",
                      backgroundColor: "#d1fae5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.25rem",
                      boxShadow: "0 2px 5px rgba(16,185,129,0.15)"
                    }}
                  >
                    🏖️
                  </div>
                </div>
                <div style={{ fontSize: "0.82rem", color: "#059669", fontWeight: "bold", marginTop: "10px", paddingTop: "8px", borderTop: "1px solid #f1f5f9" }}>
                  สิทธิคงเหลือ: {currentSelectedStaff?.remainingVacation || 0} วัน
                </div>
              </div>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <div
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderTop: "4px solid #ef4444",
                  borderRadius: "14px",
                  padding: "18px 20px",
                  boxShadow: "0 4px 12px -1px rgba(0,0,0,0.06), 0 2px 4px -2px rgba(0,0,0,0.04)",
                  marginBottom: "18px",
                  position: "relative"
                }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div style={{ fontSize: "0.88rem", color: "#000000", fontWeight: "bold" }}>ลาป่วย</div>
                    <div style={{ fontSize: "1.85rem", fontWeight: "800", color: "#ef4444", marginTop: "4px", lineHeight: 1.2 }}>
                      {(individualData?.sickDays ?? individualData?.kpi?.sickDays ?? currentSelectedStaff?.sickDays ?? 0)}{" "}
                      <span style={{ fontSize: "0.95rem", fontWeight: "bold", color: "#000000" }}>วัน</span>
                    </div>
                  </div>
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "10px",
                      backgroundColor: "#fee2e2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.25rem",
                      boxShadow: "0 2px 5px rgba(239,68,68,0.15)"
                    }}
                  >
                    🏥
                  </div>
                </div>
                <div style={{ fontSize: "0.82rem", color: "#dc2626", fontWeight: "bold", marginTop: "10px", paddingTop: "8px", borderTop: "1px solid #f1f5f9" }}>
                  เกณฑ์ไม่เกิน 30 วัน/ปี
                </div>
              </div>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <div
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderTop: "4px solid #f59e0b",
                  borderRadius: "14px",
                  padding: "18px 20px",
                  boxShadow: "0 4px 12px -1px rgba(0,0,0,0.06), 0 2px 4px -2px rgba(0,0,0,0.04)",
                  marginBottom: "18px",
                  position: "relative"
                }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div style={{ fontSize: "0.88rem", color: "#000000", fontWeight: "bold" }}>ลากิจส่วนตัว</div>
                    <div style={{ fontSize: "1.85rem", fontWeight: "800", color: "#d97706", marginTop: "4px", lineHeight: 1.2 }}>
                      {(individualData?.personalDays ?? individualData?.kpi?.personalDays ?? currentSelectedStaff?.personalDays ?? 0)}{" "}
                      <span style={{ fontSize: "0.95rem", fontWeight: "bold", color: "#000000" }}>วัน</span>
                    </div>
                  </div>
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "10px",
                      backgroundColor: "#fef3c7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.25rem",
                      boxShadow: "0 2px 5px rgba(245,158,11,0.15)"
                    }}
                  >
                    💼
                  </div>
                </div>
                <div style={{ fontSize: "0.82rem", color: "#b45309", fontWeight: "bold", marginTop: "10px", paddingTop: "8px", borderTop: "1px solid #f1f5f9" }}>
                  เกณฑ์ไม่เกิน 45 วัน/ปี
                </div>
              </div>
            </Col>
          </Grid>

          {/* ========================================================================= */}
          {/* กราฟแท่ง 12 เดือน และสัดส่วนประเภทการลา "แสดงเฉพาะเมื่อเลือกคนๆ นั้น"          */}
          {/* ========================================================================= */}
          {loadingIndividual ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#000000", fontWeight: "bold", fontSize: "1rem" }}>
              ⏳ กำลังโหลดสถิติรายเดือนและสัดส่วนการลาของ {currentSelectedStaff?.fullName}...
            </div>
          ) : (
            <Grid container spacing={2}>
              {/* กราฟแท่ง 12 เดือน ของคนๆ นั้น */}
              <Col xs={12} lg={8}>
                {renderMonthlyTrendChart(
                  individualData?.monthlyStats,
                  currentSelectedStaff?.fullName || selectedStaffUserName
                )}
              </Col>

              {/* สัดส่วนประเภทการลา ของคนๆ นั้น */}
              <Col xs={12} lg={4}>
                {renderLeaveTypeBreakdown(
                  individualData?.typeBreakdown,
                  currentSelectedStaff?.fullName || selectedStaffUserName
                )}
              </Col>
            </Grid>
          )}

          {/* ตารางประวัติการลาในปีงบประมาณนี้ ของบุคลากรรายนี้ */}
          <Card style={{ borderRadius: "14px", boxShadow: "0 4px 15px -1px rgba(0,0,0,0.07), 0 2px 6px -2px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <CardHeader style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", color: "#ffffff", padding: "14px 20px" }}>
              <div style={{ fontWeight: "bold", fontSize: "1rem", letterSpacing: "0.2px" }}>
                📋 ประวัติคำขอลา{filterMode === "fiscal" ? `ในปีงบประมาณ พ.ศ. ${selectedYear}` : ` (ช่วง ${formatThaiDateRange(customStartDate, customEndDate)})`} ของ {currentSelectedStaff?.fullName} (
                {individualData?.leaveHistory?.length || 0} รายการ)
              </div>
            </CardHeader>
            <CardBody style={{ padding: 0 }}>
              <div className="table-responsive">
                <Table hover striped style={{ margin: 0, fontSize: "0.9rem" }}>
                  <thead style={{ backgroundColor: "#f8fafc", color: "#000000", borderBottom: "2px solid #e2e8f0" }}>
                    <tr>
                      <th style={{ width: "45px", textAlign: "center", color: "#000000", fontWeight: "bold", padding: "12px 10px" }}>#</th>
                      <th style={{ color: "#000000", fontWeight: "bold", padding: "12px 10px" }}>ช่วงวันที่ลา</th>
                      <th style={{ color: "#000000", fontWeight: "bold", padding: "12px 10px" }}>ประเภทการลา</th>
                      <th style={{ textAlign: "center", color: "#000000", fontWeight: "bold", padding: "12px 10px" }}>จำนวนวัน</th>
                      <th style={{ color: "#000000", fontWeight: "bold", padding: "12px 10px" }}>เหตุผลการลา</th>
                      <th style={{ textAlign: "center", color: "#000000", fontWeight: "bold", padding: "12px 10px" }}>สถานะการอนุมัติ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {individualData?.leaveHistory && individualData.leaveHistory.length > 0 ? (
                      individualData.leaveHistory.map((lh, idx) => (
                        <tr key={idx}>
                          <td style={{ textAlign: "center", color: "#000000", fontWeight: "bold", padding: "12px 10px" }}>{idx + 1}</td>
                          <td style={{ padding: "12px 10px" }}>
                            <strong style={{ color: "#000000" }}>{formatThaiDateRange(lh.startDate, lh.endDate)}</strong>
                          </td>
                          <td style={{ padding: "12px 10px" }}>
                            <Badge
                              color={
                                lh.leaveTypeKey === 3 ? "success" : lh.leaveTypeKey === 1 ? "danger" : "warning"
                              }
                              style={{ fontSize: "0.82rem", fontWeight: "bold", padding: "5px 10px", borderRadius: "6px" }}
                            >
                              {lh.leaveTypeName || "ลา"}
                            </Badge>
                          </td>
                          <td style={{ textAlign: "center", fontWeight: "bold", color: "#000000", padding: "12px 10px" }}>{lh.totalDate} วัน</td>
                          <td style={{ color: "#000000", fontWeight: "500", padding: "12px 10px" }}>{lh.leaveDesc || "-"}</td>
                          <td style={{ textAlign: "center", padding: "12px 10px" }}>
                            {lh.isApprove === true ? (
                              <Badge color="success" style={{ fontWeight: "bold", padding: "5px 10px", borderRadius: "6px" }}>อนุมัติแล้ว</Badge>
                            ) : lh.isApprove === false ? (
                              <Badge color="danger" style={{ fontWeight: "bold", padding: "5px 10px", borderRadius: "6px" }}>ไม่อนุมัติ</Badge>
                            ) : (
                              <Badge color="secondary" style={{ fontWeight: "bold", padding: "5px 10px", borderRadius: "6px" }}>รออนุมัติ</Badge>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: "center", padding: "28px", color: "#000000", fontWeight: "bold" }}>
                          ไม่พบประวัติการลาในปีงบประมาณ พ.ศ. {selectedYear}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 6. MODAL: รายละเอียดประวัติและกราฟสถิติรายบุคคล (เมื่อคลิกดูรายคนในตาราง)      */}
      {/* ========================================================================= */}
      <Modal
        isOpen={staffModalOpen}
        toggle={() => setStaffModalOpen(false)}
        size="lg"
        style={{ fontFamily: '"Sarabun", sans-serif', maxWidth: "900px" }}
      >
        <ModalHeader
          toggle={() => setStaffModalOpen(false)}
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            color: "#ffffff",
            padding: "16px 22px",
            borderBottom: "1px solid #334155"
          }}
        >
          <div style={{ fontSize: "1.05rem", fontWeight: "bold" }}>
            👤 สถิติและประวัติการลา: {selectedStaffForModal?.fullName} {filterMode === "custom" ? `(ช่วง ${formatThaiDateRange(customStartDate, customEndDate)})` : `(ปีงบประมาณ พ.ศ. ${selectedYear})`}
          </div>
        </ModalHeader>
        <ModalBody style={{ backgroundColor: "#f8fafc", padding: "20px" }}>
          {selectedStaffForModal && (
            <div>
              {/* โปรไฟล์ */}
              <div
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "14px",
                  padding: "20px",
                  marginBottom: "18px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                }}
              >
                <div className="row">
                  <div className="col-md-6">
                    <div style={{ fontSize: "1.2rem", fontWeight: "800", color: "#000000" }}>
                      {selectedStaffForModal.fullName}
                    </div>
                    <div style={{ color: "#000000", fontSize: "0.92rem", fontWeight: "600", marginTop: "4px" }}>
                      ตำแหน่ง: <strong style={{ color: "#000000" }}>{selectedStaffForModal.positionName || "-"}</strong>
                    </div>
                    <div style={{ color: "#000000", fontSize: "0.88rem", fontWeight: "500", marginTop: "2px" }}>
                      สังกัด: {selectedStaffForModal.departmentName} (
                      {selectedStaffForModal.groupWorkName || "กลุ่มงาน"})
                    </div>
                    {selectedStaffForModal.phone && (
                      <div style={{ color: "#000000", fontSize: "0.88rem", fontWeight: "500", marginTop: "2px" }}>
                        📞 เบอร์โทร: {selectedStaffForModal.phone}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6 mt-3 mt-md-0">
                    {renderMiniLeaveDonut({
                      vacationDays: modalIndividualData?.vacationDays ?? modalIndividualData?.kpi?.vacationDays ?? selectedStaffForModal.vacationDays ?? 0,
                      sickDays: modalIndividualData?.sickDays ?? modalIndividualData?.kpi?.sickDays ?? selectedStaffForModal.sickDays ?? 0,
                      personalDays: modalIndividualData?.personalDays ?? modalIndividualData?.kpi?.personalDays ?? selectedStaffForModal.personalDays ?? 0,
                      otherDays: modalIndividualData?.otherDays ?? modalIndividualData?.kpi?.otherDays ?? selectedStaffForModal.otherDays ?? 0,
                      totalDays: modalIndividualData?.totalDays ?? modalIndividualData?.kpi?.totalDays ?? selectedStaffForModal.totalDays ?? 0
                    })}
                  </div>
                </div>
              </div>

              {/* กราฟแท่ง 12 เดือนและสัดส่วนประเภทการลาใน Modal */}
              {loadingModalData ? (
                <div style={{ textAlign: "center", padding: "30px", color: "#000000", fontWeight: "bold" }}>
                  ⏳ กำลังโหลดกราฟสถิติรายเดือน...
                </div>
              ) : (
                <div>
                  {renderMonthlyTrendChart(modalIndividualData?.monthlyStats, selectedStaffForModal.fullName)}
                  {renderLeaveTypeBreakdown(modalIndividualData?.typeBreakdown, selectedStaffForModal.fullName)}
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ backgroundColor: "#ffffff", borderTop: "1px solid #e2e8f0", padding: "14px 22px" }}>
          {selectedStaffForModal && (
            <button
              type="button"
              onClick={() => handleSelectStaffForMainView(selectedStaffForModal)}
              style={{
                background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
                color: "#ffffff",
                fontWeight: "bold",
                borderRadius: "8px",
                padding: "8px 18px",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(2,132,199,0.3)"
              }}
            >
              🔍 เปิดดูกราฟแบบเต็มหน้าจอ
            </button>
          )}
          <button
            type="button"
            onClick={() => setStaffModalOpen(false)}
            style={{
              fontWeight: "bold",
              borderRadius: "8px",
              padding: "8px 18px",
              backgroundColor: "#f1f5f9",
              color: "#0f172a",
              border: "1.5px solid #cbd5e1",
              cursor: "pointer"
            }}
          >
            ปิดหน้าต่าง
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
